import { API, api } from "@/lib/api";
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import { useDebouncedField } from "@/hooks/useDebouncedField";
import EncargosTabBar from "@/components/expediente/EncargosTabBar";
import EncargoFullView from "@/components/expediente/EncargoFullView";
import ChatwootPanel from "@/components/expediente/ChatwootPanel"; // eslint-disable-line no-unused-vars  -- reservado por si se reactiva en una vista alternativa
import SectionCard from "@/components/expediente/SectionCard";
import ExpedienteSidebar from "@/components/expediente/ExpedienteSidebar";
import VoiceDictateButton from "@/components/VoiceDictateButton";
import CatastroCard from "@/components/expediente/CatastroCard";
import {
  ChatCircleText, CheckCircle, Hourglass, Phone, MapPin, FileText,
  Lock, Warning, ArrowsOut, Calendar, UserCircle, ClipboardText,
  CurrencyEur, ClockClockwise, Eye, Plus, Minus, X as XIcon, Trash, PaperPlaneTilt,
  Users, Wrench, Notepad, ListChecks, Tag, CaretDown,
} from "@phosphor-icons/react";
import { getToken } from "@/lib/authToken";

const ESTADOS_ENCARGO = [
  { v: "pendiente",  l: "Pendiente",   hint: "Sin perito asignado"      },
  { v: "en_curso",   l: "En curso",    hint: "Perito asignado"          },
  { v: "preliminar", l: "Preliminar",  hint: "Informe preliminar emitido" },
  { v: "congelado",  l: "Congelado",   hint: "Standby · pausado en la compañía, no cuenta días en estadísticas" },
  { v: "terminado",  l: "Terminado",   hint: "Siniestro finalizado"     },
  { v: "anulado",    l: "Anulado",     hint: "Nos retiran del siniestro" },
];

const TIPOS_ENCARGO = [
  "Presencial",
  "Videoperitación asegurado",
  "Videoperitación reparador",
  "Inspección comunidad",
  "Auditoría",
  "Juicio",
];

const CAUSAS_SINIESTRO = [
  "Daños por agua",
  "Robo",
  "Incendio/Explosión/Caída de rayo",
  "Cristales",
  "Daños eléctricos",
  "Fenómenos Atmosféricos",
  "Responsabilidad Civil",
  "Otros",
];

const todayISO = () => new Date().toISOString().slice(0, 10);

// ====================================================================
// Componente principal
// ====================================================================

export default function DataTab({ exp, data, onChange, peritos, isProcessing, onUpdateExpediente, onReload, onOpenVisita }) {
  const token = getToken();
  const { user } = useAuth();
  const [aseguradoras, setAseguradoras] = useState([]);
  const [gabinetes, setGabinetes] = useState([]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/aseguradoras"),
      api.get("/gabinetes"),
    ]).then(([ra, rg]) => {
      if (!alive) return;
      setAseguradoras((ra.data || []).filter((a) => a.active !== false));
      setGabinetes((rg.data || []).filter((g) => g.active !== false));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const canMinute = can.minute(user);
  const canApprove = can.approveMinute(user);

  // Tipos de encargo extra dependientes de la aseguradora (ej. Mapfre: Consulta/Retomar)
  const aseguradoraNameActual = data.aseguradora || exp.detected_aseguradora;
  const tiposEncargoExtra = useMemo(() => {
    const out = [];
    if (aseguradoraNameActual && aseguradoras.length) {
      const a = aseguradoras.find(
        (x) => x.name === aseguradoraNameActual || x.nombre_compacto === aseguradoraNameActual,
      );
      out.push(...(a?.tipos_encargo_custom || []));
    }
    // Encargo exclusivo Santander: "Telemático".
    // Lo inyectamos siempre que la aseguradora sea Santander y no esté ya
    // configurado como tipo custom para evitar duplicados.
    if (/^santander/i.test(aseguradoraNameActual || "") && !out.some((t) => /^telem[áa]tico$/i.test(t))) {
      out.push("Telemático");
    }
    return out;
  }, [aseguradoraNameActual, aseguradoras]);

  const [activeEncargoNum, setActiveEncargoNum] = useState(1);
  // Si se elimina el encargo activo, volver al 01
  useEffect(() => {
    if (activeEncargoNum === 1) return;
    const exists = (exp.encargos_adicionales || []).some((e) => e.numero === activeEncargoNum);
    if (!exists) setActiveEncargoNum(1);
  }, [exp.encargos_adicionales, activeEncargoNum]);

  const activeEncargo = activeEncargoNum === 1
    ? null
    : (exp.encargos_adicionales || []).find((e) => e.numero === activeEncargoNum);

  if (isProcessing) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="dot-blink text-muted mb-3"><span/><span/><span/></div>
          <div className="font-display font-bold tracking-tight">
            {exp.status === "extracting" && "Extrayendo datos con IA..."}
            {exp.status === "submitting" && "Subiendo al ERP Dobilas..."}
            {exp.status === "pending" && "En cola..."}
          </div>
          <div className="text-xs text-muted mt-1">Esto suele tardar 5-15 segundos</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* ===== Cabecera FIJA del expediente =====
          Estos tres bloques quedan permanentemente visibles mientras el
          perito hace scroll por el resto del expediente:
            1. Mensajería al asegurado (WhatsApp · Chatwoot)
            2. Pestañas de encargos (01 · 02 · + Nuevo)
            3. "Datos del siniestro" + barra "Estado / Fechas"
          El resto del formulario (hitos, asignación, detalles del parte,
          asegurado, póliza, reservas, etc.) sigue scrolleándose debajo.
      */}
      <div className="flex-shrink-0 bg-background border-b border-border">
        <EncargosTabBar
          exp={exp}
          active={activeEncargoNum}
          onChange={setActiveEncargoNum}
          onReload={onReload}
        />
      </div>

      {activeEncargoNum !== 1 && activeEncargo ? (
        <EncargoFullView
          exp={exp}
          encargo={activeEncargo}
          onReload={onReload}
          onChangeTab={setActiveEncargoNum}
        />
      ) : (
      <div className="flex-1 min-w-0 overflow-y-auto bg-background min-h-0">
      <div className="px-3 md:px-6 py-4 md:py-5 flex flex-col lg:flex-row gap-5 items-start">
      <div className="flex-1 min-w-0 space-y-5">
        {/* ===== 1. Datos del encargo (movido aquí desde la cabecera sticky
            para que quede a la misma altura que la tarjeta Resumen, como
            pide el mockup). ===== */}
        <EncargoDatosCard
          exp={exp}
          data={data}
          aseguradoras={aseguradoras}
          gabinetes={gabinetes}
          tiposEncargoExtra={tiposEncargoExtra}
          aseguradoraNameActual={aseguradoraNameActual}
          onChange={onChange}
          onUpdateExpediente={onUpdateExpediente}
          onReload={onReload}
        />

        {/* ===== 2. Hitos del encargo ===== */}
        <SectionCard
          icon={ListChecks}
          tone="teal"
          title="Hitos del encargo"
          testid="section-hitos"
          padded={false}
        >
          <HitosRow
            exp={exp}
            data={data}
            peritos={peritos}
            onChange={onChange}
            onUpdateExpediente={onUpdateExpediente}
            onReload={onReload}
            onOpenVisita={onOpenVisita}
            naked
            onSavePerito={async (code) => {
              const nextData = { ...data, perito_codigo: code || null };
              onChange("perito_codigo", code || null);
              await api.put(`/expedientes/${exp.id}`, { extracted_data: nextData });
              await onReload?.();
            }}
            onClearPerito={async () => {
              onChange("perito_codigo", null);
              await api.put(`/expedientes/${exp.id}`, {
                estado_encargo: "pendiente",
                extracted_data: { ...data, perito_codigo: null },
              });
              await onReload?.();
            }}
          />
        </SectionCard>

        {/* ===== 2. Detalles del parte ===== */}
        <SectionCard
          icon={Notepad}
          tone="teal"
          title="Detalles del parte"
          testid="section-detalles-parte"
          padded={false}
        >
          <div className="px-4 md:px-5 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div>
                <label className="text-[12px] font-medium text-muted flex items-baseline gap-1.5">
                  <span>Circunstancias del encargo</span>
                  <span className="text-[10.5px] italic text-muted/80">· declaración del parte</span>
                </label>
                <textarea
                  rows={4}
                  value={data.observaciones || ""}
                  readOnly
                  data-testid="field-circunstancias-encargo"
                  className="!text-[13px] !bg-background !text-foreground !cursor-default"
                  placeholder="— Sin información de circunstancias en el parte —"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[12px] font-medium text-muted">Notas de campo del perito</label>
                  <VoiceDictateButton
                    onAppend={(chunk) => {
                      const cur = exp.observaciones_especiales || "";
                      const next = cur ? `${cur.replace(/\s+$/, "")} ${chunk}` : chunk;
                      onUpdateExpediente?.("observaciones_especiales", next);
                    }}
                    testid="notas-voice-dictate"
                  />
                </div>
                <NotasPerito
                  value={exp.observaciones_especiales}
                  onCommit={(v) => onUpdateExpediente?.("observaciones_especiales", v)}
                />
              </div>
            </div>
          </div>
          <div className="border-t border-dashed border-border" />
          <GestionesBlock
            exp={exp}
            onReload={onReload}
            currentUserId={user?.id}
            isAdminOrSenior={user?.role === "admin" || user?.role === "gestor_senior"}
            naked
          />
        </SectionCard>

        {/* ===== 4 & 5. Asegurado y riesgo · Póliza y siniestro ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <AseguradoCard data={data} onChange={onChange} />
          <PolizaCard data={data} onChange={onChange} exp={exp} onUpdateExpediente={onUpdateExpediente} peritos={peritos} onReload={onReload} />
        </div>

        {/* ===== 6 & 7. Implicados · Reparadores ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ImplicadosCard
            implicados={exp.implicados || []}
            onChange={(arr) => onUpdateExpediente?.("implicados", arr)}
          />
          <ReparadoresCard
            reparadores={exp.reparadores || []}
            onChange={(arr) => onUpdateExpediente?.("reparadores", arr)}
            aseguradoraCode={(() => {
              const a = aseguradoras.find(
                (x) => x.name === aseguradoraNameActual || x.nombre_compacto === aseguradoraNameActual,
              );
              return a?.code || null;
            })()}
          />
        </div>

        {/* La "Reserva preventiva" se gestiona desde la pestaña Informe
            (InformeTab) como bloque exclusivo del informe preliminar. */}

        {/* ===== 9 & 10. Incidencias · Ampliaciones ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <IncidenciaBlock
            exp={exp}
            tipo="incidencia"
            title="Incidencias"
            Icon={Warning}
            currentUser={user}
            onReload={onReload}
          />
          <IncidenciaBlock
            exp={exp}
            tipo="ampliacion"
            title="Ampliaciones"
            Icon={ArrowsOut}
            currentUser={user}
            onReload={onReload}
          />
        </div>

        {/* ===== Screenshot ERP (si existe) ===== */}
        {exp.dobilas_screenshot_path && (
          <div className="bg-white border-t border-border px-5 py-4">
            <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">
              Última captura del ERP
            </div>
            <img
              src={`${API}/expedientes/${exp.id}/screenshot?token=${token}`}
              alt="Screenshot Dobilas"
              className="w-full rounded-md border border-border"
              data-testid="screenshot-image"
            />
            {exp.dobilas_expediente_id && (
              <div className="mt-2 text-[11px] text-muted font-mono">
                ID Dobilas: {exp.dobilas_expediente_id}
              </div>
            )}
          </div>
        )}

        <div className="h-6" />
      </div>
      {/* ===== Columna lateral sticky (Resumen · Contacto · Pendientes) ===== */}
      <ExpedienteSidebar exp={exp} data={data} peritos={peritos} onOpenVisita={onOpenVisita} />
      </div>
      </div>
      )}
    </div>
  );
}


// ====================================================================
// EncargoDatosCard — Tarjeta unificada "Datos del encargo" (sticky)
// Combina lo que antes eran 3 secciones (HeaderSiniestro + EncargoBar +
// Asignación y tipología) en una sola tarjeta organizada en filas de 3
// columnas con buen aire, según el patrón pedido por el cliente:
//   Fila 1 (3 cols): Aseguradora · Póliza · Nº Siniestro
//   Fila 2 (3 cols): Gabinete · Nº Sin. gab. · Ramo
//   Fila 3 (3 cols): Tipo encargo · Causa · Subencargo
//   Fila 4 (línea propia): Estado (selector segmentado tipo pestañas)
//   Fila 5 (2 cols, datos discretos): Fecha encargo · Fecha ocurrencia
//   Apertura → texto pequeño en el meta de la cabecera.
// ====================================================================
function EncargoDatosCard({
  exp, data, aseguradoras = [], gabinetes = [], tiposEncargoExtra = [],
  aseguradoraNameActual, onChange, onUpdateExpediente, onReload,
}) {
  const currentAseguradora = data.aseguradora || exp.detected_aseguradora || "";

  // Aviso de presiniestro (ocurrencia vs apertura < 30 días)
  const presiniestro = (() => {
    if (!data?.fecha_siniestro || !exp?.created_at) return null;
    const occ = new Date(data.fecha_siniestro);
    const open = new Date(exp.created_at);
    if (isNaN(occ) || isNaN(open)) return null;
    const days = Math.round((open - occ) / (1000 * 60 * 60 * 24));
    if (days < 30 && days >= 0) return days;
    return null;
  })();

  // Gabinetes elegibles dada la aseguradora actual
  const gabinetesElegibles = useMemo(() => {
    if (!currentAseguradora || !gabinetes.length) return gabinetes;
    const a = aseguradoras.find(
      (x) => x.name === currentAseguradora || x.nombre_compacto === currentAseguradora,
    );
    if (!a) return [];
    return gabinetes.filter((g) => (g.aseguradoras_ids || []).includes(a.id));
  }, [gabinetes, aseguradoras, currentAseguradora]);

  // ─── Subencargo inline (dropdown visible al lado de Tipo Encargo) ───
  // Cacheamos los subencargos de la aseguradora para no hacer fetch repetidos.
  const [subencargosCache, setSubencargosCache] = useState([]);

  // Cargar subencargos de la aseguradora actual una vez
  useEffect(() => {
    if (!currentAseguradora || !aseguradoras.length) { setSubencargosCache([]); return; }
    const a = aseguradoras.find(
      (x) => x.name === currentAseguradora || x.nombre_compacto === currentAseguradora,
    );
    if (!a) { setSubencargosCache([]); return; }
    let alive = true;
    api.get(`/aseguradoras/${a.id}/subencargos`).then((r) => {
      if (!alive) return;
      setSubencargosCache(r.data?.items || []);
    }).catch(() => setSubencargosCache([]));
    return () => { alive = false; };
  }, [currentAseguradora, aseguradoras]);

  const isVideoperitacion = (data.tipo_encargo || "").toLowerCase().includes("videoperitaci");
  const subencargoOptionsForType = subencargosCache.filter(
    (s) => (s.tipo || "").toLowerCase() === (data.tipo_encargo || "").toLowerCase(),
  );
  const showSubencargoInline = isVideoperitacion && subencargoOptionsForType.length > 0;

  const handleChangeSubencargoInline = async (id) => {
    const sel = subencargoOptionsForType.find((s) => s.id === id);
    try {
      await api.put(`/expedientes/${exp.id}`, {
        subencargo_id: id || null,
        subencargo_tipo: sel?.tipo || null,
        subencargo_subtipo: sel?.subtipo || null,
      });
      onUpdateExpediente?.("subencargo_id", id || null);
      onUpdateExpediente?.("subencargo_tipo", sel?.tipo || null);
      onUpdateExpediente?.("subencargo_subtipo", sel?.subtipo || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("No se pudo guardar el subencargo", e);
    }
  };

  const handleChangeAseguradora = async (newName) => {
    if (newName === currentAseguradora) return;
    try {
      onChange("aseguradora", newName || null);
      await api.put(`/expedientes/${exp.id}`, {
        extracted_data: { ...data, aseguradora: newName || null },
        detected_aseguradora: newName || null,
        gabinete_id: null,
      });
      await onReload?.();
    } catch (e) {
      onChange?.("aseguradora", currentAseguradora || null);
      alert("No se pudo cambiar la aseguradora");
    }
  };

  const handleChangeGabinete = async (newId) => {
    try {
      await api.put(`/expedientes/${exp.id}`, { gabinete_id: newId || null });
      await onReload?.();
    } catch (e) {
      alert("No se pudo cambiar el gabinete");
    }
  };

  const apertura = exp.created_at ? new Date(exp.created_at).toLocaleDateString("es-ES") : "—";
  const numEncargo = String(exp.numero_encargo ?? 1).padStart(2, "0");
  const estado = exp.estado_encargo || "en_curso";

  const onClickEstado = async (v) => {
    if (v === estado) return;
    if (v === "pendiente") {
      onChange?.("perito_codigo", null);
      await api.put(`/expedientes/${exp.id}`, {
        estado_encargo: "pendiente",
        extracted_data: { ...data, perito_codigo: null },
      });
      await onReload?.();
    } else {
      await onUpdateExpediente?.("estado_encargo", v);
    }
  };

  return (
    <SectionCard
      icon={ClipboardText}
      tone="brand"
      title="Datos del encargo"
      testid="encargo-main-card-top"
      meta={<span className="text-[12px]">Apertura {apertura}</span>}
    >
      {/* ─── Fila 1 (3 cols): Aseguradora · Póliza · Nº Siniestro ─── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4">
        <FieldBlock label="Aseguradora" testid="hdr-aseguradora">
          <select
            data-testid="hdr-aseguradora-select"
            value={currentAseguradora}
            onChange={(e) => handleChangeAseguradora(e.target.value)}
            title="Cambiar aseguradora si la IA no la reconoció correctamente"
          >
            <option value="">— Sin asignar —</option>
            {currentAseguradora &&
              !aseguradoras.some((a) => a.name === currentAseguradora || a.nombre_compacto === currentAseguradora) && (
              <option value={currentAseguradora}>{currentAseguradora} (no catalogada)</option>
            )}
            {aseguradoras.map((a) => (
              <option key={a.id} value={a.name}>{a.name}</option>
            ))}
          </select>
        </FieldBlock>
        <FieldBlock label="Póliza" testid="hdr-poliza">
          <input
            type="text"
            value={data.numero_poliza || ""}
            onChange={(e) => onChange("numero_poliza", e.target.value)}
            placeholder="—"
            data-testid="hdr-poliza-input"
          />
        </FieldBlock>
        <FieldBlock
          label="Nº Siniestro"
          testid="hdr-siniestro"
          hint={
            presiniestro !== null
              ? `Apertura - ocurrencia: ${presiniestro} día${presiniestro === 1 ? "" : "s"} (posible presiniestro)`
              : null
          }
        >
          <input
            type="text"
            value={data.numero_siniestro || ""}
            onChange={(e) => onChange("numero_siniestro", e.target.value)}
            placeholder="—"
            data-testid="hdr-siniestro-input"
          />
        </FieldBlock>
      </div>

      {/* ─── Fila 2 (3 cols): Tipo encargo · Causa del siniestro · Ramo ─── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4 mt-4">
        <FieldBlock label="Tipo de encargo" testid="hdr-tipo-encargo">
          <select
            value={data.tipo_encargo || ""}
            onChange={(e) => onChange("tipo_encargo", e.target.value)}
            data-testid="field-tipo_encargo"
          >
            <option value="">— Seleccionar —</option>
            {TIPOS_ENCARGO.map((t) => <option key={t} value={t}>{t}</option>)}
            {tiposEncargoExtra.length > 0 && (
              <optgroup label={`Exclusivos ${aseguradoraNameActual || ""}`.trim()}>
                {tiposEncargoExtra.map((t) => <option key={`extra-${t}`} value={t}>{t}</option>)}
              </optgroup>
            )}
          </select>
        </FieldBlock>
        <FieldBlock label="Causa del siniestro" testid="hdr-causa">
          <CausaSelector data={data} onChange={onChange} naked />
        </FieldBlock>
        <FieldBlock label="Ramo" testid="hdr-ramo">
          <input
            type="text"
            value={data.ramo || ""}
            onChange={(e) => onChange("ramo", e.target.value)}
            placeholder="—"
            data-testid="hdr-ramo-input"
          />
        </FieldBlock>
      </div>

      {/* ─── Fila Subencargo (visible solo si tipo encargo es Videoperitación
            y la aseguradora tiene subencargos cargados para ese tipo) ─── */}
      {showSubencargoInline && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4 mt-4">
          <FieldBlock
            label="Subencargo"
            testid="hdr-subencargo-inline"
            hint={`Aplicable a "${data.tipo_encargo}"`}
          >
            <select
              value={exp.subencargo_id || ""}
              onChange={(e) => handleChangeSubencargoInline(e.target.value)}
              data-testid="subencargo-select-inline"
              className="!text-[13px] !py-1.5"
            >
              <option value="">— Sin clasificar —</option>
              {subencargoOptionsForType.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.subtipo ? s.subtipo : s.tipo}
                </option>
              ))}
            </select>
          </FieldBlock>
        </div>
      )}

      {/* ─── Fila 3: Estado (segmentado, línea propia) ─── */}
      <div className="mt-5">
        <div className="text-[12.5px] text-muted mb-1.5">
          Estado del encargo
        </div>
        <div
          role="tablist"
          aria-label="Estado del encargo"
          className="inline-flex items-stretch rounded-lg border border-border bg-surface-input overflow-hidden p-0.5 gap-0.5"
          data-testid="estado-encargo-toggle"
        >
          {ESTADOS_ENCARGO.map(({ v, l, hint }) => {
            const active = estado === v;
            return (
              <button
                key={v}
                role="tab"
                aria-selected={active}
                onClick={() => onClickEstado(v)}
                title={hint}
                data-testid={`estado-encargo-${v}`}
                className={`text-[13px] px-3.5 h-8 rounded-md transition-all ${
                  active
                    ? "bg-white text-brand font-semibold shadow-sm ring-1 ring-border"
                    : "bg-transparent text-muted hover:text-foreground hover:bg-white/60 font-medium"
                }`}
              >
                {l}
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Bloque plegable "Más datos" (Gabinete · Nº Sin. gabinete ·
          Subencargo · Fecha encargo · Fecha ocurrencia). Por defecto
          plegado para mantener el mockup limpio. ─── */}
      <details className="mt-4 group" data-testid="encargo-mas-datos-toggle">
        <summary className="cursor-pointer inline-flex items-center gap-1 text-[11.5px] text-muted hover:text-foreground select-none">
          <CaretDown className="w-3 h-3 transition-transform group-open:rotate-180" weight="bold" />
          <span>Más datos del encargo</span>
        </summary>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4">
          <FieldBlock label="Gabinete" testid="hdr-gabinete">
            <select
              data-testid="hdr-gabinete-select"
              value={exp.gabinete_id || ""}
              onChange={(e) => handleChangeGabinete(e.target.value)}
              disabled={!currentAseguradora || gabinetesElegibles.length === 0}
              title={
                !currentAseguradora
                  ? "Asigna primero la aseguradora"
                  : gabinetesElegibles.length === 0
                  ? "No hay gabinetes vinculados a esta aseguradora"
                  : "Cambiar gabinete intermediario"
              }
            >
              <option value="">
                {!currentAseguradora ? "—" : gabinetesElegibles.length === 0 ? "Sin gabinetes" : "— Sin asignar —"}
              </option>
              {gabinetesElegibles.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </FieldBlock>
          <FieldBlock label="Nº Siniestro gabinete" testid="hdr-siniestro-gabinete">
            <input
              type="text"
              value={data.numero_siniestro_gabinete || ""}
              onChange={(e) => onChange("numero_siniestro_gabinete", e.target.value)}
              placeholder="Opcional"
              data-testid="hdr-siniestro-gabinete-input"
              title="Referencia que asigna el gabinete externo (opcional)"
            />
          </FieldBlock>
          <FieldBlock label="Subencargo" testid="hdr-subencargo">
            <SubencargoSelector
              exp={exp}
              data={data}
              onUpdateExpediente={onUpdateExpediente}
              naked
            />
          </FieldBlock>
          <FieldBlock label="Fecha encargo" testid="hdr-fecha-encargo">
            <input
              type="date"
              value={data?.fecha_encargo || ""}
              onChange={(e) => onChange?.("fecha_encargo", e.target.value || null)}
              data-testid="encargo-fecha"
            />
          </FieldBlock>
          <FieldBlock label="Fecha siniestro" testid="hdr-fecha-siniestro" hint={presiniestro !== null ? `Apertura - ocurrencia: ${presiniestro} día${presiniestro === 1 ? "" : "s"} (posible presiniestro)` : null}>
            <input
              type="date"
              value={data?.fecha_siniestro || ""}
              onChange={(e) => onChange?.("fecha_siniestro", e.target.value || null)}
              data-testid="encargo-ocurrencia"
            />
          </FieldBlock>
          <FieldBlock label="Nº Encargo" testid="hdr-num-encargo">
            <div className="h-[38px] px-3 flex items-center text-[13px] bg-background border border-border rounded-lg text-muted tabular-nums">
              {numEncargo}
            </div>
          </FieldBlock>
        </div>
      </details>
    </SectionCard>
  );
}

/** Mini-wrapper para los campos de la tarjeta superior. Label normal en
 *  gris muted, según el mockup. */
function FieldBlock({ label, testid, hint, children }) {
  return (
    <div data-testid={testid} className="min-w-0">
      <label className="block text-[12.5px] text-muted mb-1.5">
        {label}
      </label>
      {children}
      {hint && (
        <div className="text-[10.5px] text-status-warning-fg mt-1" data-testid={`${testid}-hint`}>
          ⚠ {hint}
        </div>
      )}
    </div>
  );
}


// ====================================================================
// Cabecera: Siniestro · Póliza · Ref. asistencia · Ref. catastro
// ====================================================================

function HeaderSiniestro({ exp, data, aseguradoras = [], gabinetes = [], onChange, onUpdateExpediente, naked = false }) {
  // Calcular alerta de presiniestro: ocurrencia vs apertura < 30 días
  const presiniestro = (() => {
    if (!data?.fecha_siniestro || !exp?.created_at) return null;
    const occ = new Date(data.fecha_siniestro);
    const open = new Date(exp.created_at);
    if (isNaN(occ) || isNaN(open)) return null;
    const days = Math.round((open - occ) / (1000 * 60 * 60 * 24));
    if (days < 30 && days >= 0) return days;
    return null;
  })();

  const currentAseguradora = data.aseguradora || exp.detected_aseguradora || "";

  // Filtrar gabinetes que trabajan con la aseguradora actual
  const gabinetesElegibles = useMemo(() => {
    if (!currentAseguradora || !gabinetes.length) return gabinetes;
    const aseg = aseguradoras.find(
      (a) => a.name === currentAseguradora || a.nombre_compacto === currentAseguradora,
    );
    if (!aseg) return gabinetes;
    return gabinetes.filter((g) => (g.aseguradoras_ids || []).includes(aseg.id));
  }, [gabinetes, aseguradoras, currentAseguradora]);

  const handleChangeAseguradora = async (newName) => {
    if (newName === currentAseguradora) return;
    const v = newName || null;
    onChange?.("aseguradora", v);
    try {
      await api.put(`/expedientes/${exp.id}`, {
        detected_aseguradora: v,
        extracted_data: { ...data, aseguradora: v },
      });
    } catch (e) {
      onChange?.("aseguradora", currentAseguradora || null);
      // eslint-disable-next-line no-console
      console.error("No se pudo guardar la aseguradora", e);
    }
    onUpdateExpediente?.("detected_aseguradora", v);
  };

  const handleChangeGabinete = async (gabId) => {
    const v = gabId || null;
    if (v === (exp.gabinete_id || null)) return;
    onUpdateExpediente?.("gabinete_id", v);
    try {
      await api.put(`/expedientes/${exp.id}`, { gabinete_id: v });
    } catch (e) {
      onUpdateExpediente?.("gabinete_id", exp.gabinete_id || null);
      // eslint-disable-next-line no-console
      console.error("No se pudo guardar el gabinete", e);
    }
  };

  return (
    <div className={naked ? "px-4 md:px-5 py-2.5" : "bg-white rounded-[10px] border border-border px-4 md:px-5 py-2.5 mb-4"}>
      <div className="grid grid-cols-2 md:grid-cols-12 gap-2 md:gap-3 items-end">
      <div className="col-span-1 md:col-span-2 min-w-0">
        <label className="block text-[10.5px] font-medium text-muted mb-0.5 flex items-center gap-1">
          Siniestro · cía
          {presiniestro !== null && (
            <span
              data-testid="warning-presiniestro"
              title={`La fecha de apertura y la fecha de ocurrencia es inferior a un mes (${presiniestro} día${presiniestro === 1 ? "" : "s"}). Revisa el siniestro por si se trata de un presiniestro.`}
              className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-status-warning-bg text-status-warning-fg cursor-help"
            >
              <Warning className="w-2.5 h-2.5" weight="regular" />
            </span>
          )}
        </label>
        <input
          type="text"
          value={data.numero_siniestro || ""}
          onChange={(e) => onChange("numero_siniestro", e.target.value)}
          placeholder="—"
          data-testid="hdr-siniestro"
          className="w-full !h-8 !px-2 !text-[12px]"
        />
      </div>
      <div className="col-span-1 md:col-span-2 min-w-0">
        <label className="block text-[10.5px] font-medium text-muted mb-0.5">Siniestro · gab.</label>
        <input
          type="text"
          value={data.numero_siniestro_gabinete || ""}
          onChange={(e) => onChange("numero_siniestro_gabinete", e.target.value)}
          placeholder="Opcional"
          data-testid="hdr-siniestro-gabinete"
          title="Referencia que asigna el gabinete externo (opcional)"
          className="w-full !h-8 !px-2 !text-[12px]"
        />
      </div>
      <div className="col-span-1 md:col-span-3 min-w-0">
        <label className="block text-[10.5px] font-medium text-muted mb-0.5">Aseguradora</label>
        <select
          data-testid="hdr-aseguradora-select"
          value={currentAseguradora}
          onChange={(e) => handleChangeAseguradora(e.target.value)}
          className="w-full !h-8 !px-2 !text-[12px]"
          title="Cambiar aseguradora si la IA no la reconoció correctamente"
        >
          <option value="">— Sin asignar —</option>
          {currentAseguradora &&
            !aseguradoras.some((a) => a.name === currentAseguradora || a.nombre_compacto === currentAseguradora) && (
            <option value={currentAseguradora}>{currentAseguradora} (no catalogada)</option>
          )}
          {aseguradoras.map((a) => (
            <option key={a.id} value={a.name}>{a.name}</option>
          ))}
        </select>
      </div>
      <div className="col-span-1 md:col-span-3 min-w-0">
        <label className="block text-[10.5px] font-medium text-muted mb-0.5">Gabinete</label>
        <select
          data-testid="hdr-gabinete-select"
          value={exp.gabinete_id || ""}
          onChange={(e) => handleChangeGabinete(e.target.value)}
          disabled={!currentAseguradora || gabinetesElegibles.length === 0}
          className="w-full !h-8 !px-2 !text-[12px] disabled:opacity-50 disabled:cursor-not-allowed"
          title={
            !currentAseguradora
              ? "Asigna primero la aseguradora"
              : gabinetesElegibles.length === 0
              ? "No hay gabinetes vinculados a esta aseguradora"
              : "Cambiar gabinete intermediario"
          }
        >
          <option value="">
            {!currentAseguradora ? "—" : gabinetesElegibles.length === 0 ? "Sin gabinetes" : "— Sin asignar —"}
          </option>
          {gabinetesElegibles.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      </div>
      <HeaderField label="Póliza" value={data.numero_poliza} testid="hdr-poliza" cols={2} />
      </div>
    </div>
  );
}

function HeaderField({ label, value, testid, cols = 3 }) {
  const colCls = { 2: "md:col-span-2", 3: "md:col-span-3", 4: "md:col-span-4", 5: "md:col-span-5" }[cols] || "md:col-span-3";
  return (
    <div className={`col-span-1 ${colCls} min-w-0`}>
      <label className="block text-[10.5px] font-medium text-muted mb-0.5">{label}</label>
      <div
        data-testid={testid}
        className={`h-8 px-2 flex items-center text-[12px] bg-background border border-border rounded-lg truncate ${!value ? "text-[#9CA3AF] italic" : "text-foreground"}`}
      >
        {value || "—"}
      </div>
    </div>
  );
}

// ====================================================================
// Barra de encargo (estado + fechas)
// ====================================================================

function EncargoBar({ exp, data, onChange, onUpdateExpediente, onReload, naked = false }) {
  const num = String(exp.numero_encargo ?? 1).padStart(2, "0");
  return (
    <div className={naked
      ? "px-4 md:px-5 py-2 flex items-center gap-3 flex-wrap text-[12px]"
      : "bg-white rounded-[10px] border border-border px-4 md:px-5 py-2 flex items-center gap-3 flex-wrap text-[12px] mb-4"}>
      <div className="inline-flex items-center gap-1 bg-brand-soft text-brand-fg font-semibold text-[10.5px] rounded-full px-2 py-0.5" data-testid="encargo-badge">
        <span className="opacity-70">Enc.</span>{num}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-muted">Estado</span>
        <div className="flex gap-0 rounded-md overflow-hidden border border-border">
          {ESTADOS_ENCARGO.map(({ v, l }) => {
            const active = (exp.estado_encargo || "en_curso") === v;
            return (
              <button
                key={v}
                onClick={async () => {
                  if (v === "pendiente") {
                    onChange?.("perito_codigo", null);
                    await api.put(`/expedientes/${exp.id}`, {
                      estado_encargo: "pendiente",
                      extracted_data: { ...data, perito_codigo: null },
                    });
                    await onReload?.();
                  } else {
                    await onUpdateExpediente?.("estado_encargo", v);
                  }
                }}
                data-testid={`estado-encargo-${v}`}
                className={`text-[11px] px-2 py-1 font-medium transition-colors border-r border-border last:border-r-0 ${
                  active
                    ? "bg-brand text-white font-semibold"
                    : "bg-white text-muted hover:bg-background"
                }`}
              >{l}</button>
            );
          })}
        </div>
      </div>
      <div className="ml-auto flex items-center gap-3 text-[11px]">
        <label className="inline-flex items-center gap-1 text-muted">
          <span>Encargo</span>
          <input
            type="date"
            value={data?.fecha_encargo || ""}
            onChange={(e) => onChange?.("fecha_encargo", e.target.value || null)}
            data-testid="encargo-fecha"
            className="!h-6 !text-[11px] !px-1.5 bg-white"
          />
        </label>
        <label className="inline-flex items-center gap-1 text-muted">
          <span>Ocurrencia</span>
          <input
            type="date"
            value={data?.fecha_siniestro || ""}
            onChange={(e) => onChange?.("fecha_siniestro", e.target.value || null)}
            data-testid="encargo-ocurrencia"
            className="!h-6 !text-[11px] !px-1.5 bg-white"
          />
        </label>
        <span className="inline-flex items-center gap-1 text-muted">
          <span>Apertura</span>
          <span className="text-[12px] text-foreground font-medium" data-testid="encargo-apertura">
            {exp.created_at ? new Date(exp.created_at).toLocaleDateString("es-ES") : "—"}
          </span>
        </span>
      </div>
    </div>
  );
}

// ====================================================================
// Fila de hitos (CO/VISITA/IP/IF + Reserva + Tasado + Perito)
// ====================================================================
//
// CO  → marcaje directo (fecha)
// VIS → marcaje directo + color distinto si fecha futura (programada) vs pasada/hoy (realizada)
//       Al marcar visita, si contacto está vacío, se pone hoy automáticamente.
// IP  → abre modal PreliminarModal (requiere reserva + notas de campo para poder marcar)
// IF  → abre modal FinalModal (importe_tasacion + resumen + botón "Finalizar siniestro" que
//       calcula minuta según baremo y pasa estado a "terminado")

function isFutureDate(iso) {
  if (!iso) return false;
  try {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(iso + "T00:00:00");
    return d.getTime() > today.getTime();
  } catch { return false; }
}

function HitosRow({ exp, data, peritos, onChange, onUpdateExpediente, onSavePerito, onClearPerito, onReload, onOpenVisita, naked = false }) {
  const [ipOpen, setIpOpen] = useState(false);
  const [ifOpen, setIfOpen] = useState(false);

  // ─── Restricción bidireccional Telemático Santander ─────────────────
  // Si el expediente es Telemático de Santander, sólo puede asignarse a
  // un Tramitador telemático. Recíprocamente, los tramitadores telemáticos
  // sólo aparecen como elegibles cuando el encargo cumple esta condición.
  const tipoEnc = (data.tipo_encargo || "")
    .toLowerCase()
    .normalize("NFD").replace(/\p{Diacritic}/gu, "");
  const asegName = ((data.aseguradora || exp.detected_aseguradora) || "").toLowerCase();
  const esTelematicoSantander =
    tipoEnc.startsWith("telematico") && asegName.startsWith("santander");
  const peritosElegibles = (peritos || []).filter((p) => {
    if (p.active === false) return false;
    if (esTelematicoSantander) return p.role === "tramitador_telematico";
    return p.role !== "tramitador_telematico";
  });

  // Una visita SÓLO se considera "Realizada" cuando el perito la marca
  // explícitamente (con "Marcar hoy" si es el mismo día, con "Marcar realizada"
  // si ya tenía fecha programada, o vía móvil al pulsar "Marcar visita
  // iniciada"). Que la fecha programada haya quedado en el pasado NO basta:
  // la visita puede no haberse hecho (rehuse, cancelación, etc.).
  const visitaRealizada = exp.visita_realizada === true;
  const visitaProgramada = !!exp.fecha_visita && !visitaRealizada;

  // Estilos por hito
  const hitoCls = (done, variant) => {
    if (!done) return "bg-surface border-border";
    if (variant === "programada") return "bg-sky-50 border-sky-300 text-sky-800";
    if (variant === "done-final") return "bg-status-success-bg border-status-success-bd ring-1 ring-status-success-bd/40";
    return "bg-status-success-bg/40 border-status-success-bd";
  };

  const markVisita = (newDateISO, opts = {}) => {
    // Al marcar visita, si no hay contacto, se pone hoy. Pero la fecha elegida es la del picker.
    const patch = { fecha_visita: newDateISO || null };
    if (newDateISO && !exp.fecha_contacto) patch.fecha_contacto = todayISO();
    // Si se marca como "hoy" (botón Marcar hoy), guardamos la hora actual Y
    // marcamos la visita como REALIZADA (el perito lo ha hecho ahora mismo).
    // Si el usuario elige una fecha futura desde el picker, sólo programamos:
    // ni hora ni `visita_realizada`. Esto evita el bug de visitas programadas
    // que se daban por realizadas al pasar la fecha.
    if (opts.setTime && newDateISO) {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      patch.hora_visita = `${hh}:${mm}`;
    }
    if (opts.markRealizada) {
      patch.visita_realizada = true;
    }
    if (patch.fecha_contacto) onUpdateExpediente?.("fecha_contacto", patch.fecha_contacto);
    onUpdateExpediente?.("fecha_visita", patch.fecha_visita);
    if (patch.hora_visita) onUpdateExpediente?.("hora_visita", patch.hora_visita);
    if (patch.visita_realizada) onUpdateExpediente?.("visita_realizada", true);
  };

  // Marca una visita YA programada como realizada en este momento. Se usa
  // cuando el perito vuelve de campo de una visita que estaba programada
  // (no usa el botón "Marcar hoy" porque la fecha ya estaba puesta).
  const markRealizadaNow = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    if (!exp.hora_visita) onUpdateExpediente?.("hora_visita", `${hh}:${mm}`);
    onUpdateExpediente?.("visita_realizada", true);
  };

  const clearVisita = () => {
    onUpdateExpediente?.("fecha_visita", null);
    onUpdateExpediente?.("hora_visita", null);
    // Importante: al deshacer también reseteamos el flag de realizada.
    if (exp.visita_realizada) onUpdateExpediente?.("visita_realizada", false);
  };

  // ─── Estado de cada paso de la timeline ─────────────────────────
  //   done = paso completado (chip verde + check + fecha)
  //   current = siguiente pendiente (borde naranja discontinuo + acción)
  //   pending = paso futuro (gris vacío)
  // Visita prevista (nuevo flujo: gestor concierta fecha+hora con
  // intervalos de 15 min y se agenda automáticamente en /calendar).
  // Si no se ha pasado onOpenVisita usamos el viejo fast-path "Marcar hoy".
  const visitaPrevistaAt = exp.visita_prevista_at || null;
  const visitaHechaAt = exp.visita_hecha_at || null;
  const fmtPrevista = (() => {
    if (!visitaPrevistaAt) return null;
    try {
      const d = new Date(visitaPrevistaAt);
      if (isNaN(d)) return null;
      return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${visitaPrevistaAt.slice(11, 16)}`;
    } catch { return null; }
  })();
  const visitaAction = (() => {
    // Sin nada programado ni realizado → abrir modal o caer al fast-path
    if (!visitaPrevistaAt && !exp.fecha_visita) {
      if (onOpenVisita) {
        return { label: "Programar", onClick: onOpenVisita, testid: "step-action-visita-programar" };
      }
      return { label: "Marcar hoy", onClick: () => markVisita(todayISO(), { setTime: true, markRealizada: true }), testid: "step-action-visita-hoy" };
    }
    // Ya está programada (nuevo o legacy) y aún sin realizar → opciones para realizar / editar
    if (!visitaHechaAt && !visitaRealizada) {
      if (visitaPrevistaAt) {
        return {
          label: "Marcar realizada",
          onClick: async () => {
            try {
              await api.post(`/expedientes/${exp.id}/visita-hecha`, {});
              await onReload?.();
            } catch (e) {
              if (process.env.NODE_ENV === "development") console.debug("[hito] visita-hecha:", e);
            }
          },
          testid: "step-action-visita-realizada",
        };
      }
      if (visitaProgramada) {
        return { label: "Marcar realizada", onClick: markRealizadaNow, testid: "step-action-visita-realizada" };
      }
    }
    return null;
  })();

  const steps = [
    {
      key: "co",
      Icon: Phone,
      label: "CO",
      title: "Contacto",
      doneDate: exp.fecha_contacto || null,
      action: !exp.fecha_contacto
        ? { label: "Marcar hoy", onClick: () => onUpdateExpediente?.("fecha_contacto", todayISO()), testid: "step-action-co" }
        : null,
    },
    {
      key: "visita",
      Icon: MapPin,
      label: "Visita",
      title: "Visita",
      doneDate: visitaHechaAt || (visitaRealizada ? (exp.fecha_visita || null) : null),
      programadaDate: visitaPrevistaAt ? fmtPrevista : (visitaProgramada ? exp.fecha_visita : null),
      action: visitaAction,
    },
    {
      key: "ip",
      Icon: FileText,
      label: "IP",
      title: "Inf. Preliminar",
      doneDate: exp.fecha_preliminar || null,
      action: !exp.fecha_preliminar
        ? { label: "Emitir IP", onClick: () => setIpOpen(true), testid: "step-action-ip" }
        : null,
    },
    {
      key: "if",
      Icon: Lock,
      label: "IF",
      title: "Inf. Final",
      doneDate: exp.fecha_cierre || null,
      action: !exp.fecha_cierre
        ? { label: "Finalizar", onClick: () => setIfOpen(true), testid: "step-action-if" }
        : null,
    },
  ];

  // Identificar el paso "current": primero en orden secuencial cuyo
  // doneDate es null. Eso es lo que el perito debe atacar ahora.
  const currentIdx = steps.findIndex((s) => !s.doneDate);
  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d)) return iso;
      return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" });
    } catch { return iso; }
  };

  const peritoLabel = (() => {
    if (!data.perito_codigo) return null;
    const p = peritos.find((x) => x.code === data.perito_codigo);
    return p ? `${p.code} — ${p.name}` : data.perito_codigo;
  })();

  return (
    <div className={naked ? "px-3 md:px-5 py-3 md:py-4" : "bg-white border-b border-border px-3 md:px-5 py-3 md:py-4"}>
      {/* ===== Timeline horizontal de 4 pasos ===== */}
      <div className="flex items-start justify-between gap-2 md:gap-3" data-testid="hitos-timeline">
        {steps.map((step, idx) => {
          const isDone = !!step.doneDate;
          const isCurrent = idx === currentIdx && !isDone;
          const isProgramada = step.key === "visita" && !!step.programadaDate && !isDone;
          const isLast = idx === steps.length - 1;
          return (
            <div
              key={step.key}
              className="flex-1 min-w-0 relative flex flex-col items-center"
              data-testid={`step-${step.key}`}
              data-step-state={isDone ? "done" : isCurrent ? "current" : isProgramada ? "scheduled" : "pending"}
            >
              {/* Conector hacia el siguiente paso (línea horizontal detrás del círculo) */}
              {!isLast && (
                <div
                  aria-hidden
                  className={`absolute top-5 left-1/2 w-full h-[2px] z-0 ${
                    isDone ? "bg-status-success-fg/60" : "bg-border"
                  }`}
                />
              )}

              {/* Círculo del paso */}
              <div
                className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  isDone
                    ? "bg-status-success-fg text-white"
                    : isCurrent
                    ? "bg-white border-2 border-dashed border-brand text-brand ring-4 ring-brand-soft/50"
                    : isProgramada
                    ? "bg-sky-50 border-2 border-sky-400 text-sky-700"
                    : "bg-white border-2 border-border text-muted"
                }`}
                title={step.title}
              >
                {isDone ? (
                  <CheckCircle className="w-5 h-5" weight="fill" />
                ) : (
                  <step.Icon className="w-4 h-4" weight="regular" />
                )}
              </div>

              {/* Etiqueta del paso */}
              <div className={`mt-1.5 text-[11px] font-semibold text-center ${
                isDone ? "text-status-success-fg" : isCurrent ? "text-brand-fg" : "text-foreground"
              }`}>
                {step.title}
              </div>

              {/* Línea inferior: fecha si está done, acción si es current, estado si es programada */}
              <div className="mt-0.5 text-center min-h-[18px]">
                {isDone ? (
                  <div className="text-[10.5px] text-muted tabular-nums" data-testid={`step-${step.key}-date`}>
                    {fmtDate(step.doneDate)}
                  </div>
                ) : isCurrent && step.action ? (
                  <button
                    onClick={step.action.onClick}
                    data-testid={step.action.testid}
                    className="text-[10.5px] uppercase tracking-wider font-bold text-brand hover:text-brand-hover transition-colors"
                  >
                    {step.action.label}
                  </button>
                ) : isProgramada ? (
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[10px] text-sky-700 font-semibold">
                      Prog. {fmtDate(step.programadaDate)}
                    </span>
                    {step.action && (
                      <button
                        onClick={step.action.onClick}
                        data-testid={step.action.testid}
                        className="text-[10px] uppercase tracking-wider font-bold text-status-success-fg hover:opacity-80"
                      >
                        {step.action.label}
                      </button>
                    )}
                  </div>
                ) : step.key === "visita" && step.action ? (
                  // Visita: SIEMPRE permite "Programar" aunque no sea el paso actual.
                  // El gestor concierta la visita en cuanto recibe el siniestro.
                  <button
                    onClick={step.action.onClick}
                    data-testid={step.action.testid}
                    className="text-[10.5px] uppercase tracking-wider font-bold text-muted hover:text-brand transition-colors"
                  >
                    {step.action.label}
                  </button>
                ) : (
                  <span className="text-[10.5px] text-muted italic">—</span>
                )}
              </div>

              {/* Editor de fecha plegable (sólo visible al hacer hover/click): permite ajustar manualmente CO/Visita */}
              {(step.key === "co" || step.key === "visita") && isDone && (
                <input
                  type="date"
                  value={step.doneDate || ""}
                  onChange={(e) => {
                    if (step.key === "co") {
                      onUpdateExpediente?.("fecha_contacto", e.target.value || null);
                    } else if (step.key === "visita") {
                      markVisita(e.target.value);
                    }
                  }}
                  data-testid={`step-${step.key}-edit-date`}
                  className="mt-1 !text-[10px] !py-0 !px-1 !h-5 !w-[88px] !text-center !font-mono !bg-background"
                  title="Ajustar fecha"
                />
              )}
            </div>
          );
        })}
      </div>

      {/* ===== Tres tarjetitas: Reserva · Tasación · Perito asignado ===== */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        {/* Reserva */}
        <div
          className="bg-surface-input border border-border rounded-md px-3 py-2.5"
          data-testid="hito-card-reserva"
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-muted mb-1">
            <CurrencyEur className="w-3 h-3" weight="bold" /> Reserva
            {exp.reserva_supera_limite && (
              <span
                data-testid="warning-reserva-limite"
                title={`Reserva ${(exp.importe_reserva || 0).toFixed(2)} € supera el límite de ${(exp.reserva_limite_aplicado || 0).toFixed(2)} € de la aseguradora.`}
                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-status-error-fg text-white cursor-help"
              >
                <Warning className="w-2.5 h-2.5" weight="bold" />
              </span>
            )}
          </div>
          <MoneyInputZero
            value={exp.importe_reserva}
            onCommit={(v) => onUpdateExpediente?.("importe_reserva", v)}
            testid="field-importe_reserva"
            className={`!text-[14px] !py-1 !px-1.5 !font-mono !bg-white ${exp.reserva_supera_limite ? "!border-status-error-bd !text-status-error-fg" : ""}`}
          />
        </div>

        {/* Tasación */}
        <div
          className="bg-surface-input border border-border rounded-md px-3 py-2.5"
          data-testid="hito-card-tasacion"
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-muted mb-1">
            <CurrencyEur className="w-3 h-3" weight="bold" /> Tasación
          </div>
          <MoneyInputZero
            value={exp.importe_tasacion}
            onCommit={(v) => onUpdateExpediente?.("importe_tasacion", v)}
            testid="field-importe_tasacion-hito"
            className="!text-[14px] !py-1 !px-1.5 !font-mono !bg-white"
          />
        </div>

        {/* Perito asignado */}
        <div
          className="bg-surface-input border border-border rounded-md px-3 py-2.5"
          data-testid="hito-card-perito"
        >
          <div className="flex items-center justify-between gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-muted mb-1">
            <span className="inline-flex items-center gap-1.5">
              <UserCircle className="w-3 h-3" weight="bold" /> Perito asignado
            </span>
            {data.perito_codigo && (
              <button
                type="button"
                onClick={() => {
                  if (confirm("¿Quitar perito asignado? El expediente volverá a estado Pendiente.")) {
                    onClearPerito?.();
                  }
                }}
                data-testid="clear-perito-button"
                title="Quitar perito · pasar a pendiente"
                className="normal-case tracking-normal text-[10px] text-muted hover:text-status-error-fg font-medium"
              >
                Quitar
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={data.perito_codigo || ""}
              onChange={(e) => onSavePerito?.(e.target.value)}
              data-testid="field-perito_codigo"
              className="!text-[13px] !py-1 !px-2 !h-8 !font-medium flex-1 !bg-white"
            >
              <option value="">— Sin asignar —</option>
              {peritosElegibles.map((p) => (
                <option key={p.id} value={p.code}>{p.code} — {p.name}</option>
              ))}
            </select>
          </div>
          {esTelematicoSantander && (
            <div className="mt-1 text-[10.5px] text-status-info-fg italic" data-testid="perito-picker-hint-telematico">
              Sólo Tramitadores Telemáticos disponibles (encargo Telemático · Santander).
            </div>
          )}
          {peritoLabel && (exp.supervisores?.length > 0) && (
            <div className="mt-1.5 pt-1.5 border-t border-dashed border-border">
              <div className="text-[9px] uppercase tracking-widest font-semibold text-muted mb-0.5">
                Supervisan
              </div>
              <SupervisoresPicker
                peritos={peritos}
                currentPeritoCode={data.perito_codigo}
                value={exp.supervisores || []}
                onChange={(arr) => onUpdateExpediente?.("supervisores", arr)}
              />
            </div>
          )}
          {peritoLabel && !(exp.supervisores?.length > 0) && (
            <div className="mt-1">
              <SupervisoresPicker
                peritos={peritos}
                currentPeritoCode={data.perito_codigo}
                value={exp.supervisores || []}
                onChange={(arr) => onUpdateExpediente?.("supervisores", arr)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Botón "Ctrl+Z" — Restaurar informe final cuando IF está hecho.
          Visible y prominente, con tono ámbar para indicar acción reversible
          pero destructiva. Borra PDF, minuta y vuelve al estado anterior. */}
      {exp.fecha_cierre && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={async () => {
              const min = exp.importe_minuta ? ` (minuta: ${Number(exp.importe_minuta).toFixed(2)} €)` : "";
              if (!confirm(
                `¿Restaurar el informe final?${min}\n\n` +
                `· Se borrará el PDF del informe final\n` +
                `· Se borrará la minuta calculada\n` +
                `· El expediente volverá al estado anterior\n\n` +
                `Tasación e importes editables se mantienen para que puedas corregirlos.`
              )) return;
              try {
                await api.post(`/expedientes/${exp.id}/unfinalize`);
                onReload?.();
              } catch (e) {
                alert(e?.response?.data?.detail || "No se ha podido restaurar el informe");
              }
            }}
            data-testid="hito-undo-finalize"
            title="Restaurar informe final (Ctrl+Z): borra el PDF y la minuta, y reabre el expediente"
            className="text-[12px] inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-status-warning-bd bg-status-warning-bg/40 text-status-warning-fg font-semibold hover:bg-status-warning-bg transition-colors"
          >
            <ClockClockwise className="w-3.5 h-3.5" weight="bold" />
            <span>Restaurar informe final</span>
            <kbd className="ml-1 text-[9.5px] font-mono font-bold bg-white/80 text-status-warning-fg border border-status-warning-bd/60 px-1 py-px rounded">
              Ctrl+Z
            </kbd>
          </button>
        </div>
      )}

      {ipOpen && (
        <PreliminarModal
          exp={exp}
          onClose={() => setIpOpen(false)}
          onDone={() => { setIpOpen(false); onReload?.(); }}
          onUpdateExpediente={onUpdateExpediente}
        />
      )}
      {ifOpen && (
        <FinalModal
          exp={exp}
          data={data}
          onClose={() => setIfOpen(false)}
          onDone={() => { setIfOpen(false); onReload?.(); }}
        />
      )}
    </div>
  );
}

// ====================================================================
// PreliminarModal — pide reserva preventiva + notas de campo
// ====================================================================

function PreliminarModal({ exp, onClose, onDone, onUpdateExpediente }) {
  const [reserva, setReserva] = useState(
    exp.importe_reserva != null && exp.importe_reserva !== 0
      ? String(exp.importe_reserva)
      : "",
  );
  const [notas, setNotas] = useState(exp.observaciones_especiales || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = notas.trim().length > 0 && reserva !== "" && !isNaN(parseFloat(reserva));

  const save = async () => {
    if (!canSubmit) {
      setError("Necesitas rellenar las notas de campo y la reserva preventiva para emitir el IP.");
      return;
    }
    setSaving(true); setError("");
    try {
      await api.put(`/expedientes/${exp.id}`, {
        importe_reserva: parseFloat(reserva),
        observaciones_especiales: notas,
        fecha_preliminar: todayISO(),
      });
      onDone?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} title="Emitir Informe Preliminar" Icon={FileText} width="max-w-xl">
      <div className="space-y-4 p-5">
        <div>
          <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted">Reserva preventiva €</label>
          <input
            type="number" step="0.01"
            value={reserva}
            onChange={(e) => setReserva(e.target.value)}
            data-testid="ip-modal-reserva"
            placeholder="0,00"
            className="!font-mono !text-[14px] !py-2"
            autoFocus
          />
          <div className="text-[10.5px] text-muted mt-1 normal-case tracking-normal">
            Se reflejará en el campo <span className="font-mono font-bold">€ Res.</span> del expediente.
          </div>
        </div>

        <div>
          <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted">
            Notas de campo del perito <span className="text-status-error-fg">· obligatorio</span>
          </label>
          <textarea
            rows={6}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            data-testid="ip-modal-notas"
            placeholder="Descripción técnica del siniestro, causa real, elementos afectados, alcance..."
            className="!text-[12px]"
          />
          <div className="text-[10.5px] text-muted mt-1 normal-case tracking-normal">
            {notas.trim().length > 0
              ? `${notas.length} caracteres`
              : "Las notas de campo son obligatorias para emitir el IP."}
          </div>
        </div>

        {error && <div className="text-[12px] text-status-error-fg">{error}</div>}
      </div>

      <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-surface rounded-b-lg">
        <button
          onClick={onClose}
          data-testid="ip-modal-cancel"
          className="text-[12px] px-3 py-1.5 rounded-md border border-border bg-white hover:bg-surface font-medium"
        >Cancelar</button>
        <button
          onClick={save}
          disabled={saving || !canSubmit}
          data-testid="ip-modal-submit"
          className="text-[12px] px-4 py-1.5 rounded-md bg-brand text-white font-semibold hover:bg-brand-hover disabled:opacity-50"
        >{saving ? "Guardando..." : "Emitir IP · Marcar hoy"}</button>
      </div>
    </ModalShell>
  );
}

// ====================================================================
// FinalModal — resumen + tasación + calcular minuta + finalizar
// ====================================================================

function FinalModal({ exp, data, onClose, onDone }) {
  // Sugerir importe_tasacion = total de la valoración del siniestro si aún
  // no hay tasación fijada. El perito siempre puede sobrescribirlo.
  const valoracionTotal = (() => {
    const grupos = exp.valoracion_grupos || [];
    let s = 0;
    for (const g of grupos) {
      if (g.excluir) continue;
      let sub = (g.partidas || []).reduce((acc, p) => {
        const cant = Number(p?.cantidad || 0);
        const pu = Number(p?.precio_unitario || 0);
        let bruto = cant * pu;
        const dep = Number(p?.depreciacion_pct || 0);
        if (dep) bruto *= Math.max(0, 1 - dep / 100);
        const iva = Number(p?.iva_pct || 0);
        if (iva) bruto *= 1 + iva / 100;
        return acc + bruto;
      }, 0);
      if (g.limite != null && g.limite >= 0) sub = Math.min(sub, Number(g.limite));
      const fr = Number(g.franquicia || 0);
      if (fr) {
        if (g.franquicia_tipo === "pct") sub *= Math.max(0, 1 - fr / 100);
        else sub = Math.max(0, sub - fr);
      }
      s += sub;
    }
    return Math.round(s * 100) / 100;
  })();

  const [tasacion, setTasacion] = useState(() => {
    if (exp.importe_tasacion != null && exp.importe_tasacion !== 0) return String(exp.importe_tasacion);
    if (valoracionTotal > 0) return String(valoracionTotal);
    return "";
  });
  const [usedValoracion] = useState(
    (exp.importe_tasacion == null || exp.importe_tasacion === 0) && valoracionTotal > 0,
  );
  const [rehuse, setRehuse] = useState(exp.rehuse || "ninguno");
  const [rehuseMotivo, setRehuseMotivo] = useState(exp.rehuse_motivo || "");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Desplazamiento (km). Editable; se persiste en `km_desplazamiento`.
  const [km, setKm] = useState(exp.km_desplazamiento != null ? String(exp.km_desplazamiento) : "");
  const [kmSuggesting, setKmSuggesting] = useState(false);
  const [kmReason, setKmReason] = useState("");
  const kmNum = parseFloat(km);
  const kmValid = !isNaN(kmNum) && kmNum >= 0;

  const tasacionNum = parseFloat(tasacion);
  const tasacionValid = !isNaN(tasacionNum) && tasacionNum > 0;

  // Auto-calcular minuta al cambiar la tasación o km (debounce de 400ms)
  useEffect(() => {
    if (!tasacionValid) { setPreview(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data: r } = await api.get(`/expedientes/${exp.id}/minuta-preview`, {
          params: { importe_tasacion: tasacionNum, km: kmValid ? kmNum : 0 },
        });
        setPreview(r);
      } catch {
        setPreview({ importe_minuta: null, reason: "Error consultando baremo" });
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [tasacion, km]); // eslint-disable-line react-hooks/exhaustive-deps

  const suggestKm = async () => {
    setKmSuggesting(true); setKmReason("");
    try {
      const { data: r } = await api.get(`/expedientes/${exp.id}/km-desplazamiento/sugerencia`);
      if (r.km != null) {
        setKm(String(r.km));
        setKmReason("");
      } else {
        setKmReason(r.reason || "No se pudo calcular");
      }
    } catch {
      setKmReason("Error consultando el geocodificador");
    } finally { setKmSuggesting(false); }
  };

  const finalize = async () => {
    if (!tasacionValid) {
      setError("El importe de tasación debe ser mayor que 0");
      return;
    }
    if (!confirm("¿Finalizar el siniestro? Se grabará la minuta y el expediente pasará a 'Terminado'. Esta acción se puede deshacer editando los hitos.")) return;
    setSubmitting(true); setError("");
    try {
      const { data: r } = await api.post(`/expedientes/${exp.id}/finalize`, {
        importe_tasacion: tasacionNum,
        rehuse,
        rehuse_motivo: rehuse === "ninguno" ? null : rehuseMotivo.trim() || null,
      });
      if (r.minuta_calculated) {
        alert(`Siniestro finalizado. Minuta grabada: ${r.importe_minuta?.toFixed(2)} €`);
      } else {
        alert("Siniestro finalizado. No se ha podido calcular la minuta automáticamente (sin baremo aplicable). Revísala manualmente.");
      }
      onDone?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error finalizando");
    } finally {
      setSubmitting(false);
    }
  };

  const causantes = (exp.implicados || []).filter((x) => x.tipo === "causante");
  const perjudicados = (exp.implicados || []).filter((x) => x.tipo === "perjudicado");

  return (
    <ModalShell onClose={onClose} title="Finalizar siniestro — Informe Final" Icon={Lock} width="max-w-2xl">
      {/* Resumen de datos clave */}
      <div className="px-5 pt-5 pb-3">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted mb-2">Resumen del siniestro</div>
        <div className="rounded-md border border-border bg-surface px-4 py-3 text-[12px] space-y-1.5">
          <Row label="Nº Siniestro" value={data.numero_siniestro} mono />
          <Row label="Aseguradora" value={data.aseguradora || exp.detected_aseguradora} />
          <Row label="Asegurado" value={data.asegurado_nombre} />
          <Row label="Causa" value={data.causa} />
          <Row label="Tipo encargo" value={data.tipo_encargo} />
          <Row label="Fecha siniestro" value={data.fecha_siniestro} mono />
          <Row label="Fecha apertura" value={exp.created_at?.slice(0, 10)} mono />
          <Row label="Visita" value={exp.fecha_visita ? `${exp.fecha_visita}${exp.hora_visita ? ` · ${exp.hora_visita}` : ""}` : "—"} mono />
          <Row label="Reserva preventiva" value={exp.importe_reserva != null ? `${Number(exp.importe_reserva).toFixed(2)} €` : "—"} mono />
          {(causantes.length > 0 || perjudicados.length > 0) && (
            <Row
              label="Implicados"
              value={[
                causantes.length ? `${causantes.length} causante${causantes.length === 1 ? "" : "s"}` : null,
                perjudicados.length ? `${perjudicados.length} perjudicado${perjudicados.length === 1 ? "" : "s"}` : null,
              ].filter(Boolean).join(" · ")}
            />
          )}
          {(exp.reparadores || []).length > 0 && (
            <Row label="Reparadores" value={`${exp.reparadores.length} asignado${exp.reparadores.length === 1 ? "" : "s"}`} />
          )}
          {exp.observaciones_especiales && (
            <div className="pt-1.5 mt-1.5 border-t border-border">
              <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1">Notas de campo</div>
              <div className="text-[12px] text-foreground italic whitespace-pre-wrap line-clamp-4">
                "{exp.observaciones_especiales}"
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Importe tasación + preview de minuta */}
      <div className="px-5 pb-4 pt-2 space-y-3">
        <div>
          <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted">
            Importe tasación definitivo €
          </label>
          <input
            type="number" step="0.01"
            value={tasacion}
            onChange={(e) => setTasacion(e.target.value)}
            data-testid="if-modal-tasacion"
            placeholder="0,00"
            className="!font-mono !text-[14px] !py-2"
            autoFocus
          />
          {usedValoracion && (
            <div className="mt-1.5 text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="tasacion-from-valoracion">
              ⓘ Pre-rellenado desde la pestaña Valoración ({valoracionTotal.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}). Edítalo si procede.
            </div>
          )}
        </div>

        {/* Rehúse — naranja (parcial) / rojo (total) */}
        <div data-testid="if-modal-rehuse">
          <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted !mb-1.5 block">
            Rehúse del siniestro
          </label>
          <div className="grid grid-cols-3 gap-2">
            <RehuseOption
              active={rehuse === "ninguno"}
              onClick={() => setRehuse("ninguno")}
              label="Sin rehúse"
              tint="neutral"
              testid="rehuse-ninguno"
            />
            <RehuseOption
              active={rehuse === "parcial"}
              onClick={() => setRehuse("parcial")}
              label="Rehúse parcial"
              tint="orange"
              testid="rehuse-parcial"
            />
            <RehuseOption
              active={rehuse === "total"}
              onClick={() => setRehuse("total")}
              label="Rehúse total"
              tint="red"
              testid="rehuse-total"
            />
          </div>
          {rehuse !== "ninguno" && (
            <>
              <div className={`mt-2 text-[11px] flex items-start gap-1.5 ${
                rehuse === "parcial" ? "text-orange-700" : "text-red-700"
              }`}>
                <Warning className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" weight="bold" />
                <span>
                  El informe se marcará con un banner{" "}
                  <b>{rehuse === "parcial" ? "naranja" : "rojo"}</b> en el PDF y el marco en la página pública de verificación.
                </span>
              </div>
              <div className="mt-3">
                <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted !mb-1 block">
                  Motivo del rehúse <span className="normal-case tracking-normal italic text-muted/80">· opcional, pero recomendado</span>
                </label>
                <textarea
                  rows={3}
                  value={rehuseMotivo}
                  onChange={(e) => setRehuseMotivo(e.target.value)}
                  data-testid="if-modal-rehuse-motivo"
                  placeholder={rehuse === "total"
                    ? "Ej. El siniestro no está cubierto por la póliza (cláusula 4.2 de exclusiones)."
                    : "Ej. Se indemnizan daños de fontanería. No se cubre la sustitución del solado por estar fuera de cobertura."}
                  className={`!text-[12px] !bg-white ${
                    rehuse === "parcial" ? "!border-orange-300 focus:!border-orange-500" : "!border-red-300 focus:!border-red-500"
                  }`}
                />
              </div>
            </>
          )}
        </div>

        <div
          data-testid="if-modal-minuta-preview"
          className={`rounded-md border px-3 py-2.5 text-[12px] ${
            preview?.importe_minuta != null
              ? "border-status-success-bd bg-status-success-bg text-status-success-fg"
              : "border-border bg-surface text-muted"
          }`}
        >
          {!tasacionValid ? (
            <span className="italic">Introduce un importe de tasación para calcular la minuta.</span>
          ) : loading ? (
            <span>Calculando minuta según baremo...</span>
          ) : preview?.importe_minuta != null ? (
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold">Minuta calculada</div>
                  <div className="text-[10.5px] normal-case tracking-normal mt-0.5 text-muted">
                    Baremo: <span className="font-semibold">{preview.baremo_name}</span>
                    {preview.tipo_encargo_baremo && <> · {preview.tipo_encargo_baremo}</>}
                  </div>
                </div>
                <div className="font-mono font-bold text-[20px]">
                  {preview.importe_minuta.toFixed(2)} €
                </div>
              </div>
              {preview.importe_desplazamiento > 0 && (
                <div className="text-[10.5px] normal-case tracking-normal text-muted border-t border-status-success-bd/40 pt-1.5" data-testid="if-modal-minuta-desp">
                  Honorarios: <b>{preview.importe_base.toFixed(2)} €</b> + Desplazamiento: <b>{preview.km} km × {preview.precio_km.toFixed(2)} €/km = {preview.importe_desplazamiento.toFixed(2)} €</b>
                </div>
              )}
            </div>
          ) : (
            <span className="italic">
              {preview?.reason || "Sin baremo aplicable — la minuta deberá revisarse manualmente."}
            </span>
          )}
        </div>

        {/* Desplazamiento (km) — sólo visible si el baremo aplicable lo paga */}
        {tasacionValid && preview?.precio_km != null && preview?.tipo_encargo_baremo !== "videoperitacion" && (
          <div className="rounded-md border border-border bg-white px-3 py-2.5" data-testid="if-modal-km-block">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label htmlFor="km_desplazamiento" className="text-[11px] uppercase tracking-widest font-semibold text-muted">
                Desplazamiento (km)
              </label>
              <button
                type="button"
                onClick={suggestKm}
                disabled={kmSuggesting}
                data-testid="if-modal-km-suggest"
                className="text-[10.5px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 disabled:opacity-60"
                title="Calcular automáticamente desde la dirección del gabinete a la del expediente"
              >
                {kmSuggesting ? "Calculando…" : "📍 Sugerir"}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="km_desplazamiento"
                type="number"
                step="0.1"
                min="0"
                value={km}
                onChange={(e) => setKm(e.target.value)}
                placeholder="0"
                data-testid="if-modal-km"
                className="!text-[12px] !bg-white max-w-[120px]"
              />
              <span className="text-[11px] text-muted">km · ida (se aplica el baremo {preview.precio_km.toFixed(2)} €/km)</span>
            </div>
            {kmReason && <div className="text-[10.5px] text-status-warning-fg mt-1">{kmReason}</div>}
          </div>
        )}

        {error && <div className="text-[12px] text-status-error-fg">{error}</div>}
      </div>

      <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-surface rounded-b-lg">
        <button
          onClick={onClose}
          data-testid="if-modal-cancel"
          className="text-[12px] px-3 py-1.5 rounded-md border border-border bg-white hover:bg-surface font-medium"
        >Cancelar</button>
        <button
          onClick={finalize}
          disabled={submitting || !tasacionValid}
          data-testid="if-modal-finalize"
          className="text-[12px] px-4 py-1.5 rounded-md bg-foreground text-white font-semibold hover:bg-foreground/90 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <Lock className="w-3.5 h-3.5" weight="bold" />
          {submitting ? "Finalizando..." : "Finalizar siniestro"}
        </button>
      </div>
    </ModalShell>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[10px] uppercase tracking-widest text-muted font-semibold w-[130px] flex-shrink-0">{label}</span>
      <span className={`text-[12.5px] text-foreground ${mono ? "font-mono" : ""} truncate`}>{value || "—"}</span>
    </div>
  );
}

function RehuseOption({ active, onClick, label, tint, testid }) {
  // Paleta: neutral / naranja / rojo
  const activeCls = {
    neutral: "bg-slate-800 border-slate-800 text-white",
    orange:  "bg-orange-500 border-orange-500 text-white",
    red:     "bg-red-600 border-red-600 text-white",
  }[tint];
  const idleCls = {
    neutral: "bg-white border-border text-muted hover:bg-surface",
    orange:  "bg-white border-orange-200 text-orange-700 hover:bg-orange-50",
    red:     "bg-white border-red-200 text-red-700 hover:bg-red-50",
  }[tint];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`w-full text-[11.5px] uppercase tracking-wider font-semibold px-3 py-2 rounded-md border-2 transition-colors ${
        active ? activeCls : idleCls
      }`}
    >
      {label}
    </button>
  );
}

function ModalShell({ children, onClose, title, Icon, width = "max-w-lg" }) {
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="modal-backdrop"
    >
      <div
        className={`w-full ${width} bg-white rounded-lg shadow-2xl border border-border max-h-[92vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="inline-flex items-center gap-2 font-display font-bold text-[14px] tracking-tight text-foreground">
            {Icon && <Icon className="w-4 h-4 text-brand" weight="bold" />}
            {title}
          </div>
          <button
            onClick={onClose}
            data-testid="modal-close"
            className="p-1 text-muted hover:text-foreground"
          >
            <XIcon className="w-4 h-4" weight="bold" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ====================================================================
// Cascada de supervisores
// ====================================================================

function SupervisoresPicker({ peritos, currentPeritoCode, value, onChange }) {
  const [open, setOpen] = useState(false);
  // Solo gestores senior y otros peritos (excluyendo al perito asignado y a gestor_siniestros)
  const candidates = (peritos || []).filter(
    (p) =>
      p.active !== false &&
      p.code !== currentPeritoCode &&
      ["gestor_senior", "perito_interno", "perito_externo"].includes(p.role),
  );
  const set = new Set(value);

  const toggle = (code) => {
    if (set.has(code)) {
      onChange(value.filter((c) => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  return (
    <div className="relative" data-testid="supervisores-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="supervisores-toggle"
        className="text-left w-full text-[12px] py-1 px-2 rounded-md border border-border bg-white hover:border-border-strong transition-colors min-h-[28px]"
      >
        {value.length === 0 ? (
          <span className="text-muted italic">— Sin supervisores —</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {value.map((code) => {
              const p = (peritos || []).find((x) => x.code === code);
              return (
                <span
                  key={code}
                  className="inline-flex items-center gap-1 bg-brand-soft text-brand-fg rounded-full px-2 py-0.5 text-[10px] font-mono font-bold"
                >
                  {code}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggle(code); }}
                    className="hover:text-status-error-fg"
                  >
                    <XIcon className="w-2.5 h-2.5" weight="bold" />
                  </button>
                </span>
              );
            })}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-border rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="supervisores-dropdown">
          {candidates.length === 0 ? (
            <div className="text-[11px] text-muted px-3 py-2 italic">No hay candidatos disponibles</div>
          ) : (
            candidates.map((p) => (
              <label
                key={p.id}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface cursor-pointer text-[12px] normal-case tracking-normal"
                data-testid={`sup-option-${p.code}`}
              >
                <input
                  type="checkbox"
                  checked={set.has(p.code)}
                  onChange={() => toggle(p.code)}
                  className="!w-3.5 !h-3.5 !p-0"
                />
                <span className="font-mono font-bold text-[11px]">{p.code}</span>
                <span className="text-muted">{p.name}</span>
                <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${
                  p.role === "gestor_senior" ? "bg-brand text-white" : "bg-surface text-muted"
                }`}>
                  {p.role === "gestor_senior" ? "Senior" : p.role.replace("perito_", "")}
                </span>
              </label>
            ))
          )}
          <div className="border-t border-border px-3 py-1.5 flex justify-end">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[10px] text-muted hover:text-foreground"
            >Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ====================================================================
// Selector de causa
// ====================================================================

function CausaSelector({ data, onChange, naked = false }) {
  const causa = data.causa || "";
  const isStandard = CAUSAS_SINIESTRO.includes(causa);
  const isOtros = !!causa && !isStandard;
  const [otrosMode, setOtrosMode] = useState(isOtros);

  return (
    <div data-testid="causa-selector">
      {!naked && (
        <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold flex items-center justify-between">
          <span>
            Causa siniestro
            {data.aseguradora && <span className="text-brand-fg ml-1">· {data.aseguradora}</span>}
          </span>
        </label>
      )}
      <select
        value={otrosMode || isOtros ? "Otros" : causa}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "Otros") {
            setOtrosMode(true);
            if (isStandard) onChange("causa", "");
          } else {
            setOtrosMode(false);
            onChange("causa", v);
          }
        }}
        data-testid="field-causa"
        className="!text-[13px] !py-1.5"
      >
        <option value="">— Seleccionar —</option>
        {CAUSAS_SINIESTRO.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      {(otrosMode || isOtros) && (
        <input
          type="text"
          value={isOtros ? causa : ""}
          onChange={(e) => onChange("causa", e.target.value)}
          placeholder="Especifica la causa..."
          data-testid="field-causa-otros"
          className="!text-[12px] mt-2"
          autoFocus
        />
      )}
    </div>
  );
}


// ====================================================================
// Tarjetas asegurado y póliza
// ====================================================================

function AseguradoCard({ data, onChange }) {
  return (
    <SectionCard
      icon={UserCircle}
      tone="blue"
      title="Asegurado y riesgo"
      testid="asegurado-card"
    >
      <Field label="Nombre" value={data.asegurado_nombre} onChange={(v) => onChange("asegurado_nombre", v)} testid="field-asegurado_nombre" big />
      <div className="grid grid-cols-2 gap-3 mt-2">
        <Field label="NIF" value={data.asegurado_nif} onChange={(v) => onChange("asegurado_nif", v)} testid="field-asegurado_nif" />
        <Field label="Teléfono" value={data.asegurado_telefono} onChange={(v) => onChange("asegurado_telefono", v)} testid="field-asegurado_telefono" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Móvil" value={data.asegurado_movil} onChange={(v) => onChange("asegurado_movil", v)} testid="field-asegurado_movil" />
        <Field label="Email" value={data.asegurado_email} onChange={(v) => onChange("asegurado_email", v)} testid="field-asegurado_email" />
      </div>
      <div className="mt-3 pt-3 border-t border-dashed border-border">
        <Field label="Dirección del riesgo" value={data.direccion_riesgo} onChange={(v) => onChange("direccion_riesgo", v)} testid="field-direccion_riesgo" big />
        <div className="grid grid-cols-3 gap-3 mt-2">
          <Field label="C.P." value={data.codigo_postal} onChange={(v) => onChange("codigo_postal", v)} testid="field-codigo_postal" />
          <Field label="Municipio" value={data.municipio} onChange={(v) => onChange("municipio", v)} testid="field-municipio" />
          <Field label="Provincia" value={data.provincia} onChange={(v) => onChange("provincia", v)} testid="field-provincia" />
        </div>
      </div>
      {(data.tipo_encargo || "").toLowerCase().includes("inspecci")
        && (data.tipo_encargo || "").toLowerCase().includes("comunidad") && (
        <div
          className="mt-3 pt-3 border-t border-dashed border-border space-y-2"
          data-testid="comunidad-extra-fields"
        >
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted">
            Datos de la comunidad
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Presidente" value={data.presidente_nombre} onChange={(v) => onChange("presidente_nombre", v)} testid="field-presidente_nombre" />
            <Field label="Tel. Presidente" value={data.presidente_telefono} onChange={(v) => onChange("presidente_telefono", v)} testid="field-presidente_telefono" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Administrador" value={data.administrador_nombre} onChange={(v) => onChange("administrador_nombre", v)} testid="field-administrador_nombre" />
            <Field label="Tel. Administrador" value={data.administrador_telefono} onChange={(v) => onChange("administrador_telefono", v)} testid="field-administrador_telefono" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Año construcción" value={data.anyo_construccion} onChange={(v) => onChange("anyo_construccion", v)} testid="field-anyo_construccion" />
            <Field label="Nº plantas" value={data.num_plantas} onChange={(v) => onChange("num_plantas", v)} testid="field-num_plantas" />
            <Field label="Nº viviendas" value={data.num_viviendas} onChange={(v) => onChange("num_viviendas", v)} testid="field-num_viviendas" />
          </div>
          <div className="text-[10.5px] text-muted leading-snug">
            Si dejas estos campos en blanco, el informe los completará
            automáticamente con los datos del Catastro de España.
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function PolizaCard({ data, onChange, exp, onUpdateExpediente, onReload }) {
  return (
    <SectionCard
      icon={ClipboardText}
      tone="brand"
      title="Póliza y siniestro"
      testid="poliza-card"
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nº Póliza" value={data.numero_poliza} onChange={(v) => onChange("numero_poliza", v)} testid="field-numero_poliza" mono />
        <Field label="Ref. asistencia" value={data.referencia_asistencia} onChange={(v) => onChange("referencia_asistencia", v)} testid="field-referencia_asistencia" mono />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nº Siniestro" value={data.numero_siniestro} onChange={(v) => onChange("numero_siniestro", v)} testid="field-numero_siniestro" mono />
        <Field
          label="Nº Siniestro gabinete"
          value={data.numero_siniestro_gabinete}
          onChange={(v) => onChange("numero_siniestro_gabinete", v)}
          testid="field-numero_siniestro_gabinete"
          mono
          placeholder="Opcional · ref. del gabinete externo"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Ramo" value={data.ramo} onChange={(v) => onChange("ramo", v)} testid="field-ramo" />
        <div>
          <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold">Urgente</label>
          <select
            value={data.urgente ? "true" : "false"}
            onChange={(e) => onChange("urgente", e.target.value === "true")}
            data-testid="field-urgente"
            className="!text-[13px] !py-1"
          >
            <option value="false">No</option>
            <option value="true">Sí</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Fecha efecto" value={data.fecha_efecto_poliza} onChange={(v) => onChange("fecha_efecto_poliza", v)} testid="field-fecha_efecto_poliza" type="date" />
        <Field label="Fecha siniestro" value={data.fecha_siniestro} onChange={(v) => onChange("fecha_siniestro", v)} testid="field-fecha_siniestro" type="date" />
      </div>
      <CatastroCard exp={exp} onReload={onReload} />
    </SectionCard>
  );
}

// ====================================================================
// Bloque económico (minuta + supervisión)
// ====================================================================

function EconomicoBlock({ exp, onUpdateExpediente, canMinute, canApprove, onReload }) {
  return (
    <section className="bg-white border-t border-border px-5 py-4" data-testid="economico-block">
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-3 pb-2 border-b border-border">
        Económico — análisis y márgenes
      </div>
      {exp.minuta_supervision_pending && (
        <div className="mb-3 rounded-md border border-brand/30 bg-brand-soft/50 px-3 py-2.5 text-[12px]" data-testid="minuta-pending-banner">
          <div className="flex items-start gap-2">
            <Hourglass className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" weight="bold" />
            <div className="flex-1">
              <div className="font-semibold text-foreground">Pendiente de supervisión para minutar</div>
              {exp.minuta_supervision_note && (
                <div className="text-muted mt-0.5 italic">"{exp.minuta_supervision_note}"</div>
              )}
              {canApprove && <ApproveMinuta expId={exp.id} onDone={onReload} />}
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label htmlFor="importe_tasacion">Tasación €</label>
          <MoneyInput
            id="importe_tasacion"
            value={exp.importe_tasacion}
            onCommit={(v) => onUpdateExpediente?.("importe_tasacion", v)}
            testid="field-importe_tasacion"
          />
        </div>
        <div>
          <label htmlFor="importe_minuta" className="flex items-center justify-between gap-2">
            <span>Minuta €</span>
            {exp.importe_minuta_manual && (
              <span
                title="Minuta editada manualmente. /finalize NO la recalculará por baremo. Pulsa la cruz para volver al cálculo automático."
                data-testid="minuta-manual-badge"
                className="inline-flex items-center gap-1 text-[9px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
              >
                MANUAL
                <button
                  type="button"
                  onClick={() => onUpdateExpediente?.("importe_minuta", null)}
                  data-testid="minuta-reset-baremo"
                  className="ml-0.5 hover:text-red-700"
                  title="Limpiar para que se recalcule por baremo al finalizar"
                >×</button>
              </span>
            )}
          </label>
          <MoneyInput
            id="importe_minuta"
            value={exp.importe_minuta}
            onCommit={(v) => onUpdateExpediente?.("importe_minuta", v)}
            testid="field-importe_minuta"
            disabled={!canMinute}
            placeholder={canMinute ? "ej. 87.50" : "—"}
          />
        </div>
        <div>
          <label htmlFor="coste_directo">Coste €</label>
          <MoneyInput
            id="coste_directo"
            value={exp.coste_directo}
            onCommit={(v) => onUpdateExpediente?.("coste_directo", v)}
            testid="field-coste_directo"
          />
        </div>
      </div>
      {exp.importe_minuta != null && exp.coste_directo != null && exp.importe_minuta > 0 && (
        <div className="mt-3 text-xs text-muted normal-case tracking-normal">
          Margen: <span className="font-semibold text-foreground">
            {(exp.importe_minuta - exp.coste_directo).toFixed(2)}€
          </span>{" "}
          <span className="text-status-success-fg font-semibold">
            ({(((exp.importe_minuta - exp.coste_directo) / exp.importe_minuta) * 100).toFixed(1)}%)
          </span>
        </div>
      )}
      {!canMinute && !exp.minuta_supervision_pending && (
        <RequestMinuta expId={exp.id} onDone={onReload} />
      )}
      {exp.minuta_approved_by && !exp.minuta_supervision_pending && exp.importe_minuta && (
        <div className="mt-3 text-[11px] text-status-success-fg inline-flex items-center gap-1.5" data-testid="minuta-approved-tag">
          <CheckCircle className="w-3.5 h-3.5" weight="fill" /> Minuta aprobada por supervisión
        </div>
      )}
    </section>
  );
}

// ====================================================================
// Helpers UI
// ====================================================================

function Field({ label, value, onChange, testid, mono, big, type = "text", placeholder }) {
  return (
    <div>
      <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        placeholder={placeholder}
        className={`${big ? "!text-[14px] !font-bold" : "!text-[12px]"} ${mono ? "!font-mono" : ""} !py-1`}
      />
    </div>
  );
}

function TtrField({ label, value, onChange, testid }) {
  const state = value === true ? "yes" : value === false ? "no" : "none";
  return (
    <div className="md:col-span-2 col-span-2">
      <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold">{label}</label>
      <div className="flex gap-0 rounded-md overflow-hidden border border-border w-fit mt-0.5" data-testid={testid}>
        {[
          { s: "none", l: "—",  v: null  },
          { s: "yes",  l: "Sí", v: true  },
          { s: "no",   l: "No", v: false },
        ].map(({ s, l, v }) => (
          <button
            key={s}
            onClick={() => onChange(v)}
            className={`text-[11px] px-2.5 py-1 font-medium transition-colors ${
              state === s ? "bg-foreground text-white font-semibold" : "text-muted bg-white hover:text-foreground"
            }`}
          >{l}</button>
        ))}
      </div>
    </div>
  );
}

function ObsBlock({ title, value, onChange, testid, maxChars, placeholder }) {
  const v = value || "";
  const over = maxChars && v.length > maxChars;
  return (
    <div className="bg-white border-b border-border px-5 py-4">
      <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold flex items-center justify-between">
        <span>{title}</span>
        {maxChars && (
          <span className={`font-mono normal-case tracking-normal ${over ? "text-status-error-fg" : "text-muted"}`}>
            {v.length} / {maxChars}
          </span>
        )}
      </label>
      <textarea
        rows={maxChars ? 1 : 5}
        value={v}
        onChange={(e) => {
          if (maxChars && e.target.value.length > maxChars) return;
          onChange(e.target.value);
        }}
        data-testid={testid}
        className="!text-[12px] font-mono"
        placeholder={placeholder}
      />
    </div>
  );
}

// ====================================================================
// Marcadores especiales (incidencia, ampliación) — se reusan
// ====================================================================

// Notas internas del perito — entrada con debounce para que el textarea fluya
function NotasPerito({ value, onCommit }) {
  const [val, onChange, flush] = useDebouncedField(value || "", onCommit);
  return (
    <textarea
      rows={4}
      value={val}
      onChange={onChange}
      onBlur={flush}
      data-testid="field-notas-perito"
      className="!text-[12px]"
      placeholder="Notas internas del perito tras la visita / inspección. Pulsa Dictar para usar la voz."
    />
  );
}

// Input numérico (€) con debounce — vacío = null. Convierte a parseFloat.
function MoneyInput({ id, value, onCommit, disabled, placeholder, testid }) {
  const [val, onChange, flush] = useDebouncedField(
    value == null ? "" : String(value),
    (v) => onCommit(v === "" ? null : parseFloat(v)),
  );
  return (
    <input
      id={id}
      type="number"
      step="0.01"
      value={val}
      onChange={onChange}
      onBlur={flush}
      data-testid={testid}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}

// Variante con default 0 (en lugar de null) — para hitos con importe siempre presente.
function MoneyInputZero({ value, onCommit, className, testid }) {
  const [val, onChange, flush] = useDebouncedField(
    value == null ? "0" : String(value),
    (v) => onCommit(v === "" ? 0 : parseFloat(v)),
  );
  return (
    <input
      type="number"
      step="0.01"
      value={val}
      onChange={onChange}
      onBlur={flush}
      data-testid={testid}
      className={className}
    />
  );
}

function SpecialMarker({ exp, flagKey, descKey, label, Icon, accent, onUpdateExpediente }) {
  const checked = !!exp[flagKey];
  // Campo con debounce: la textarea responde instantánea pero solo se persiste
  // 500ms tras dejar de teclear (evita lag por PUT por cada letra).
  const [desc, onDescChange, flushDesc] = useDebouncedField(
    exp[descKey] || "",
    (v) => onUpdateExpediente?.(descKey, v),
  );
  const accentCls = accent === "status-error"
    ? "border-status-error-bd bg-status-error-bg text-status-error-fg"
    : "border-amber-300 bg-amber-50 text-amber-700";

  return (
    <div className={`p-3 rounded-md border ${checked ? accentCls : "border-border bg-white"}`}>
      <label className="flex items-center gap-2 cursor-pointer !mb-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onUpdateExpediente?.(flagKey, e.target.checked)}
          data-testid={`flag-${flagKey}`}
          className="!w-3.5 !h-3.5 !p-0"
        />
        <Icon className="w-3.5 h-3.5" weight="bold" />
        <span className="text-[12px] font-semibold">{label}</span>
      </label>
      {checked && (
        <textarea
          rows={2}
          placeholder="Describe brevemente..."
          value={desc}
          onChange={onDescChange}
          onBlur={flushDesc}
          data-testid={`desc-${flagKey}`}
          className="!text-[11px] mt-2 !bg-white"
        />
      )}
    </div>
  );
}

// ====================================================================
// IncidenciaBlock — Q&A entre gestor (pregunta) y perito (respuesta)
// ====================================================================

function formatDT(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function IncidenciaBlock({ exp, tipo, title, Icon, currentUser, onReload }) {
  const items = (exp.incidencias || []).filter((x) => x.tipo === tipo);
  const pendingCount = items.filter((x) => !x.respuesta).length;
  const canOpen = can.openIncidencia(currentUser);
  const singular = tipo === "incidencia" ? "incidencia" : "ampliación";

  const [draft, setDraft] = useState("");
  const [targetType, setTargetType] = useState("perito");
  const [targetUserId, setTargetUserId] = useState("");
  const [slaHours, setSlaHours] = useState("");
  const [defaultSla, setDefaultSla] = useState(tipo === "incidencia" ? 24 : 48);
  const [gestores, setGestores] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const tone = tipo === "incidencia" ? "red" : "amber";
  const accentBg = tipo === "incidencia" ? "bg-status-error-bg/50" : "bg-status-warning-bg/50";
  const accentBorder = tipo === "incidencia" ? "border-status-error-bd" : "border-status-warning-bd";

  // Cargar SLA defaults + lista de gestores una sola vez.
  useEffect(() => {
    if (!canOpen) return;
    let cancelled = false;
    api.get("/settings/company").then((r) => {
      if (cancelled) return;
      const v = tipo === "incidencia" ? r.data?.incidencias_sla_h : r.data?.ampliaciones_sla_h;
      if (v && Number(v) > 0) setDefaultSla(Number(v));
    }).catch(() => {});
    api.get("/peritos").then((r) => {
      if (cancelled) return;
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      const onlyGestores = list.filter((u) => ["admin", "gestor_senior", "gestor_siniestros"].includes(u.role));
      setGestores(onlyGestores);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [canOpen, tipo]);

  const submit = async () => {
    const pregunta = draft.trim();
    if (!pregunta) return;
    if (targetType === "otro_gestor" && !targetUserId) {
      setError("Selecciona el gestor destinatario");
      return;
    }
    setSending(true); setError("");
    try {
      const body = { tipo, pregunta, target_type: targetType };
      if (targetType === "otro_gestor") body.target_user_id = targetUserId;
      if (slaHours) body.sla_hours = parseInt(slaHours, 10);
      await api.post(`/expedientes/${exp.id}/incidencias`, body);
      setDraft("");
      setTargetType("perito");
      setTargetUserId("");
      setSlaHours("");
      await onReload?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error abriendo aviso");
    } finally {
      setSending(false);
    }
  };

  const submitLabel =
    sending ? "Enviando..." :
    targetType === "self" ? `Crear ${singular} (resuelvo yo)` :
    targetType === "otro_gestor" ? `Asignar a gestor` :
    `Abrir y notificar al perito`;

  return (
    <SectionCard
      icon={Icon}
      tone={tone}
      title={title}
      testid={`incidencia-block-${tipo}`}
      meta={
        <>
          {pendingCount > 0 && (
            <span
              className="inline-flex items-center gap-1 bg-status-error-bg text-status-error-fg text-[10px] font-semibold px-1.5 py-0.5 rounded"
              data-testid={`incidencia-${tipo}-pending-badge`}
            >
              {pendingCount} pendiente{pendingCount === 1 ? "" : "s"}
            </span>
          )}
          <span className="text-[11px] tabular-nums">
            {items.length} registro{items.length === 1 ? "" : "s"}
          </span>
        </>
      }
    >
      {/* Form para abrir nueva (solo gestor/admin) */}
      {canOpen && (
        <div className={`rounded-md border ${accentBorder} ${accentBg} px-3 py-2.5 mb-3`}>
          <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted !mb-1 block">
            Abrir nueva {singular}
          </label>
          <textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            data-testid={`incidencia-${tipo}-new-pregunta`}
            placeholder={`Escribe la ${singular}...`}
            className="!text-[12px] !bg-white"
          />

          {/* Destinatario en su propia fila (los 3 chips, con wrap libre) */}
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`incidencia-${tipo}-target-radios`}>
            {[
              { k: "perito",      label: "Perito asignado" },
              { k: "self",        label: "Resuelvo yo" },
              { k: "otro_gestor", label: "Otro gestor" },
            ].map((opt) => (
              <button
                type="button"
                key={opt.k}
                onClick={() => setTargetType(opt.k)}
                data-testid={`incidencia-${tipo}-target-${opt.k}`}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border transition-colors ${
                  targetType === opt.k
                    ? "bg-foreground text-white border-foreground"
                    : "bg-white text-foreground border-border hover:border-foreground"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Selector de gestor — sólo visible cuando aplica para no robar
              espacio innecesario en la tarjeta. */}
          {targetType === "otro_gestor" && (
            <div className="mt-2">
              <select
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                data-testid={`incidencia-${tipo}-target-user-select`}
                className="!h-8 !text-[12px] !px-2 !bg-white border border-border rounded-md w-full"
              >
                <option value="">Selecciona gestor…</option>
                {gestores.map((g) => (
                  <option key={g.id} value={g.id}>{g.name || g.code}</option>
                ))}
              </select>
            </div>
          )}

          {error && <div className="text-[11px] text-status-error-fg mt-2">{error}</div>}

          {/* SLA + botón de envío en la misma fila al pie de la tarjeta.
              SLA es compacto (90px) y el botón ocupa el resto. */}
          <div className="flex items-center gap-2 mt-2">
            <div className="relative w-[90px] flex-shrink-0">
              <input
                type="number"
                min={1}
                max={720}
                value={slaHours}
                onChange={(e) => setSlaHours(e.target.value)}
                placeholder={`${defaultSla}h`}
                data-testid={`incidencia-${tipo}-sla-hours`}
                title={`SLA — horas para responder (por defecto ${defaultSla}h)`}
                className="!h-8 !text-[12px] !pl-7 !pr-1 !bg-white border border-border rounded-md text-center w-full"
              />
              <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest font-semibold text-muted">
                SLA
              </span>
            </div>
            <button
              onClick={submit}
              disabled={sending || !draft.trim() || (targetType === "otro_gestor" && !targetUserId)}
              data-testid={`incidencia-${tipo}-submit`}
              className="flex-1 text-[11px] inline-flex items-center justify-center gap-1.5 px-3 h-8 rounded-md border border-border bg-white text-foreground font-semibold hover:bg-background transition-colors disabled:opacity-50"
            >
              <PaperPlaneTilt className="w-3.5 h-3.5" weight="regular" />
              <span className="truncate">{submitLabel}</span>
            </button>
          </div>
        </div>
      )}

      {/* Lista de items existentes */}
      {items.length === 0 ? (
        <div className="text-[12px] text-muted italic py-1">
          {canOpen
            ? `Sin ${title.toLowerCase()} registradas.`
            : `Sin ${title.toLowerCase()} pendientes. El gestor no te ha planteado ninguna.`}
        </div>
      ) : (
        <ul className="space-y-2.5">
          {items.map((it) => (
            <IncidenciaItemRow
              key={it.id}
              expId={exp.id}
              tipo={tipo}
              item={it}
              currentUser={currentUser}
              onReload={onReload}
            />
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function IncidenciaItemRow({ expId, tipo, item, currentUser, onReload }) {
  const [answerDraft, setAnswerDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const isAuthor = item.created_by === currentUser?.id;
  const isAdminOrSenior = currentUser?.role === "admin" || currentUser?.role === "gestor_senior";
  const canDelete = isAuthor || isAdminOrSenior;

  const answer = async () => {
    const respuesta = answerDraft.trim();
    if (!respuesta) return;
    setSending(true); setError("");
    try {
      await api.post(`/expedientes/${expId}/incidencias/${item.id}/answer`, { respuesta });
      setAnswerDraft("");
      await onReload?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error respondiendo");
    } finally {
      setSending(false);
    }
  };

  const remove = async () => {
    if (!confirm(`¿Eliminar esta ${tipo === "incidencia" ? "incidencia" : "ampliación"}?`)) return;
    try {
      await api.delete(`/expedientes/${expId}/incidencias/${item.id}`);
      await onReload?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error eliminando");
    }
  };

  const pending = !item.respuesta;

  return (
    <li
      data-testid={`incidencia-item-${item.id}`}
      className={`rounded-md border px-3 py-2.5 ${pending ? "border-amber-300 bg-amber-50/40" : "border-border bg-surface"}`}
    >
      {/* Pregunta */}
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 text-[10px] uppercase tracking-widest font-bold text-muted mt-0.5 w-5">P.</div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] text-foreground whitespace-pre-wrap">{item.pregunta}</div>
          <div className="text-[10.5px] text-muted mt-1 normal-case tracking-normal flex items-center gap-1.5 flex-wrap">
            <UserCircle className="w-3 h-3" weight="bold" />
            <span className="font-semibold">{item.created_by_name || "Gestor"}</span>
            <span className="text-border">·</span>
            <Calendar className="w-3 h-3" weight="bold" />
            <span>{formatDT(item.created_at)}</span>
            {item.target_type && item.target_type !== "perito" && (
              <>
                <span className="text-border">·</span>
                <span className="inline-flex items-center gap-1 text-indigo-700 font-semibold uppercase tracking-wider text-[10px]">
                  {item.target_type === "self"
                    ? "Resuelve gestor"
                    : `Asignada a ${item.target_user_name || item.target_user_code || "gestor"}`}
                </span>
              </>
            )}
            {item.resolved_by_self && (
              <>
                <span className="text-border">·</span>
                <span className="inline-flex items-center gap-1 text-indigo-700 font-semibold uppercase tracking-wider text-[10px]">
                  Resuelta por gestor
                </span>
              </>
            )}
            {pending && item.due_at && (
              <>
                <span className="text-border">·</span>
                <span className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wider text-[10px] ${
                  new Date(item.due_at).getTime() < Date.now() ? "text-rose-700" : "text-amber-700"
                }`}>
                  <Hourglass className="w-3 h-3" weight="bold" />
                  {new Date(item.due_at).getTime() < Date.now()
                    ? "Vencida"
                    : `SLA ${item.sla_hours || 24}h`}
                </span>
              </>
            )}
            {pending && !item.due_at && (
              <>
                <span className="text-border">·</span>
                <span className="inline-flex items-center gap-1 text-amber-700 font-semibold uppercase tracking-wider text-[10px]">
                  <Hourglass className="w-3 h-3" weight="bold" /> Pendiente respuesta
                </span>
              </>
            )}
            {(item.reclamaciones?.length || 0) > 0 && (
              <>
                <span className="text-border">·</span>
                <span className="inline-flex items-center gap-1 text-orange-700 font-semibold uppercase tracking-wider text-[10px]">
                  {item.reclamaciones.length} reclamación{item.reclamaciones.length === 1 ? "" : "es"}
                </span>
              </>
            )}
          </div>
        </div>
        {canDelete && (
          <button
            onClick={remove}
            data-testid={`incidencia-${item.id}-remove`}
            className="text-muted hover:text-status-error-fg p-1 flex-shrink-0"
            title="Eliminar"
          >
            <Trash className="w-3.5 h-3.5" weight="bold" />
          </button>
        )}
      </div>

      {/* Respuesta o formulario */}
      {item.respuesta ? (
        <div className="flex items-start gap-2 mt-2 pt-2 border-t border-border/70">
          <div className="flex-shrink-0 text-[10px] uppercase tracking-widest font-bold text-status-success-fg mt-0.5 w-5">R.</div>
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] text-foreground whitespace-pre-wrap">{item.respuesta}</div>
            <div className="text-[10.5px] text-muted mt-1 normal-case tracking-normal flex items-center gap-1.5">
              <CheckCircle className="w-3 h-3 text-status-success-fg" weight="fill" />
              <span className="font-semibold">{item.answered_by_name || "Perito"}</span>
              <span className="text-border">·</span>
              <Calendar className="w-3 h-3" weight="bold" />
              <span>{formatDT(item.answered_at)}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-2 pt-2 border-t border-border/70">
          <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted !mb-1 block">
            Tu respuesta
          </label>
          <textarea
            rows={2}
            value={answerDraft}
            onChange={(e) => setAnswerDraft(e.target.value)}
            data-testid={`incidencia-${item.id}-respuesta`}
            placeholder="Escribe tu respuesta..."
            className="!text-[12px] !bg-white"
          />
          {error && <div className="text-[11px] text-status-error-fg mt-1">{error}</div>}
          <div className="flex justify-end mt-2">
            <button
              onClick={answer}
              disabled={sending || !answerDraft.trim()}
              data-testid={`incidencia-${item.id}-answer-submit`}
              className="text-[11px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-900 text-white font-semibold transition-colors disabled:opacity-50"
            >
              <PaperPlaneTilt className="w-3.5 h-3.5" weight="bold" />
              {sending ? "Enviando..." : "Responder"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ====================================================================
// Minuta: solicitar / aprobar
// ====================================================================

function RequestMinuta({ expId, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      await api.post(`/expedientes/${expId}/request-minuta`, { note });
      setOpen(false);
      setNote("");
      onDone?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error solicitando supervisión");
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="request-minuta-button"
        className="mt-3 text-[11px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-brand/40 text-brand-fg bg-brand-soft hover:bg-brand/15 font-semibold"
      >
        <ChatCircleText className="w-3.5 h-3.5" weight="bold" /> Solicitar minuta a supervisión
      </button>
    );
  }
  return (
    <div className="mt-3 border border-brand/30 rounded-md p-3 bg-brand-soft/40">
      <label className="!text-[11px] !font-semibold !text-muted">Nota para el supervisor (opcional)</label>
      <textarea
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid="request-minuta-note"
        className="!text-[12px]"
        placeholder="ej. siniestro complejo, agua afecta a 3 viviendas..."
      />
      <div className="flex justify-end gap-2 mt-2">
        <button onClick={() => setOpen(false)} className="text-[11px] px-3 py-1 text-muted">Cancelar</button>
        <button
          onClick={submit}
          disabled={sending}
          data-testid="request-minuta-confirm"
          className="text-[11px] px-3 py-1 rounded-md bg-brand text-white font-semibold disabled:opacity-50"
        >Enviar solicitud</button>
      </div>
    </div>
  );
}

function ApproveMinuta({ expId, onDone }) {
  const [importe, setImporte] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!importe) return;
    setSending(true);
    try {
      await api.post(`/expedientes/${expId}/approve-minuta`, { importe_minuta: parseFloat(importe) });
      onDone?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error aprobando minuta");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-2 flex items-center gap-2">
      <input
        type="number"
        step="0.01"
        value={importe}
        onChange={(e) => setImporte(e.target.value)}
        placeholder="Importe minuta €"
        data-testid="approve-minuta-input"
        className="!text-[12px] !py-1 flex-1"
      />
      <button
        onClick={submit}
        disabled={!importe || sending}
        data-testid="approve-minuta-button"
        className="text-[11px] px-3 py-1 rounded-md bg-brand text-white font-semibold disabled:opacity-50 whitespace-nowrap"
      >Aprobar</button>
    </div>
  );
}


// ====================================================================
// Gestiones del encargo (timeline de comentarios)
// ====================================================================

function GestionesBlock({ exp, onReload, currentUserId, isAdminOrSenior, naked = false }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const gestiones = (exp.gestiones || []).slice().sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || "")
  );

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/expedientes/${exp.id}/gestiones`, { text: t });
      setText("");
      setOpen(false);
      await onReload?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error añadiendo comentario");
    } finally {
      setSending(false);
    }
  };

  const remove = async (gid) => {
    if (!confirm("¿Eliminar esta gestión?")) return;
    try {
      await api.delete(`/expedientes/${exp.id}/gestiones/${gid}`);
      await onReload?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error eliminando");
    }
  };

  const numEncargo = String(exp.numero_encargo ?? 1).padStart(2, "0");

  return (
    <div className={naked ? "px-5 py-4" : "bg-white border-b border-border px-5 py-4"} data-testid="gestiones-block">
      <div className="flex items-center justify-between mb-3">
        <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold !mb-0">
          Gestiones del encargo {numEncargo}{" "}
          <span className="font-mono text-foreground normal-case tracking-normal">
            ({gestiones.length})
          </span>
        </label>
        {!open ? (
          <button
            onClick={() => setOpen(true)}
            data-testid="add-gestion-button"
            className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md bg-foreground text-white font-semibold hover:bg-foreground/90"
          >
            <Plus className="w-3 h-3" weight="bold" /> Añadir
          </button>
        ) : (
          <button
            onClick={() => { setOpen(false); setText(""); }}
            className="text-[11px] text-muted hover:text-foreground"
          >Cancelar</button>
        )}
      </div>

      {/* Editor */}
      {open && (
        <div className="mb-3 border border-border rounded-md p-2.5 bg-surface/40" data-testid="gestion-editor">
          <textarea
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="gestion-text"
            autoFocus
            className="!text-[12px]"
            placeholder="Describe brevemente la gestión: llamada, visita, contacto, novedad..."
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={submit}
              disabled={!text.trim() || sending}
              data-testid="gestion-submit"
              className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-md bg-brand text-white font-semibold disabled:opacity-50"
            >
              {sending ? "Enviando..." : (<><PaperPlaneTilt className="w-3 h-3" weight="bold" /> Publicar</>)}
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {gestiones.length === 0 ? (
        <div className="text-[12px] text-muted italic py-3 text-center normal-case tracking-normal">
          No hay gestiones registradas. Pulsa "Añadir" para crear la primera.
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {gestiones.map((g) => {
            const canDelete = isAdminOrSenior || g.created_by === currentUserId;
            return (
              <div
                key={g.id}
                data-testid={`gestion-${g.id}`}
                className="bg-surface/60 border border-border rounded-md p-3 group hover:bg-surface transition-colors"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 flex-wrap text-[10px]">
                    <span className="font-mono font-bold text-brand-fg">{g.created_by_code || "—"}</span>
                    <span className="text-muted">{g.created_by_name || ""}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted font-mono whitespace-nowrap">
                      {new Date(g.created_at).toLocaleString("es-ES", {
                        day: "2-digit", month: "2-digit", year: "2-digit",
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </span>
                    {canDelete && (
                      <button
                        onClick={() => remove(g.id)}
                        data-testid={`gestion-delete-${g.id}`}
                        className="opacity-0 group-hover:opacity-100 p-0.5 text-muted hover:text-status-error-fg transition-opacity"
                        title="Eliminar"
                      >
                        <Trash className="w-3 h-3" weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-[12px] text-foreground whitespace-pre-wrap normal-case tracking-normal leading-relaxed">
                  {g.text}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ====================================================================
// Implicados (lista de causantes/perjudicados)
// ====================================================================

const newImplicado = () => ({
  id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
  tipo: "perjudicado",
  nombre: "", nif: "", telefono: "", email: "", direccion: "",
  aseguradora: "", poliza: "", notas: "",
});

function ImplicadosCard({ implicados, onChange }) {
  const add = () => onChange([...(implicados || []), newImplicado()]);
  const update = (idx, key, value) => {
    const next = implicados.slice();
    next[idx] = { ...next[idx], [key]: value };
    onChange(next);
  };
  const remove = (idx) => {
    if (!confirm("¿Eliminar este implicado?")) return;
    onChange(implicados.filter((_, i) => i !== idx));
  };

  return (
    <SectionCard
      icon={Users}
      tone="gray"
      title="Implicados"
      testid="implicados-card"
      meta={
        <button
          onClick={add}
          data-testid="add-implicado"
          className="text-[11px] inline-flex items-center gap-1 px-2 h-7 rounded-md border border-border bg-white text-foreground hover:bg-background"
        >
          <Plus className="w-3 h-3" weight="bold" /> Añadir
        </button>
      }
    >
      {(!implicados || implicados.length === 0) ? (
        <div className="text-[12px] text-muted italic py-1">No hay implicados registrados.</div>
      ) : (
        <div className="space-y-3">
          {implicados.map((it, idx) => (
            <div
              key={it.id || idx}
              data-testid={`implicado-${idx}`}
              className="bg-white border border-slate-200 rounded-md p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex gap-0 rounded-md overflow-hidden border border-slate-200">
                  {["causante", "perjudicado"].map((t) => (
                    <button
                      key={t}
                      onClick={() => update(idx, "tipo", t)}
                      data-testid={`implicado-${idx}-tipo-${t}`}
                      className={`text-[10px] uppercase tracking-wider px-2.5 py-0.5 font-semibold transition-colors ${
                        it.tipo === t
                          ? (t === "causante" ? "bg-amber-600 text-white" : "bg-slate-700 text-white")
                          : "text-muted hover:bg-surface"
                      }`}
                    >{t}</button>
                  ))}
                </div>
                <button
                  onClick={() => remove(idx)}
                  data-testid={`implicado-${idx}-remove`}
                  className="text-muted hover:text-status-error-fg p-1"
                >
                  <Trash className="w-3 h-3" weight="bold" />
                </button>
              </div>
              <Field label="Nombre" value={it.nombre} onChange={(v) => update(idx, "nombre", v)} testid={`impl-${idx}-nombre`} big />
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Field label="NIF" value={it.nif} onChange={(v) => update(idx, "nif", v)} testid={`impl-${idx}-nif`} mono />
                <Field label="Teléfono" value={it.telefono} onChange={(v) => update(idx, "telefono", v)} testid={`impl-${idx}-tel`} mono />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Email" value={it.email} onChange={(v) => update(idx, "email", v)} testid={`impl-${idx}-email`} />
                <Field label="Dirección" value={it.direccion} onChange={(v) => update(idx, "direccion", v)} testid={`impl-${idx}-direccion`} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Aseguradora" value={it.aseguradora} onChange={(v) => update(idx, "aseguradora", v)} testid={`impl-${idx}-aseg`} />
                <Field label="Póliza" value={it.poliza} onChange={(v) => update(idx, "poliza", v)} testid={`impl-${idx}-poliza`} mono />
              </div>
              <Field label="Notas" value={it.notas} onChange={(v) => update(idx, "notas", v)} testid={`impl-${idx}-notas`} />
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ====================================================================
// Reparadores (proveedores externos)
// ====================================================================

const newReparador = () => ({
  id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
  empresa: "", contacto: "", especialidad: "",
  telefono: "", email: "", cif: "",
  importe_presupuesto: null, notas: "",
});

function ReparadoresCard({ reparadores, onChange, aseguradoraCode }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerForIdx, setPickerForIdx] = useState(null); // null = nueva; idx = vincular existente

  const add = () => onChange([...(reparadores || []), newReparador()]);
  const update = (idx, key, value) => {
    const next = reparadores.slice();
    next[idx] = { ...next[idx], [key]: value };
    onChange(next);
  };
  const remove = (idx) => {
    if (!confirm("¿Eliminar este reparador?")) return;
    onChange(reparadores.filter((_, i) => i !== idx));
  };

  const handlePick = (dirRep) => {
    const built = {
      id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
      empresa: dirRep.nombre || "",
      contacto: "",
      especialidad: dirRep.especialidad || "",
      telefono: (dirRep.telefonos || [])[0] || "",
      email: (dirRep.emails || [])[0] || "",
      cif: dirRep.cif || "",
      importe_presupuesto: null,
      notas: dirRep.provincia ? `Provincia: ${dirRep.provincia}` : "",
      directorio_id: dirRep.id,
    };
    if (pickerForIdx === null) {
      onChange([...(reparadores || []), built]);
    } else {
      const next = reparadores.slice();
      next[pickerForIdx] = { ...next[pickerForIdx], ...built, id: next[pickerForIdx].id };
      onChange(next);
    }
    setPickerOpen(false);
    setPickerForIdx(null);
  };

  return (
    <SectionCard
      icon={Wrench}
      tone="gray"
      title="Reparadores"
      testid="reparadores-card"
      meta={
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => { setPickerForIdx(null); setPickerOpen(true); }}
            data-testid="pick-reparador-directorio"
            className="text-[11px] inline-flex items-center gap-1 px-2 h-7 rounded-md border border-border bg-white text-foreground hover:bg-background"
            title="Buscar reparador en el directorio"
          >
            <ChatCircleText className="w-3 h-3" weight="regular" /> Directorio
          </button>
          <button
            onClick={add}
            data-testid="add-reparador"
            className="text-[11px] inline-flex items-center gap-1 px-2 h-7 rounded-md border border-border bg-white text-foreground hover:bg-background"
          >
            <Plus className="w-3 h-3" weight="bold" /> Añadir
          </button>
        </div>
      }
    >
      {(!reparadores || reparadores.length === 0) ? (
        <div className="text-[12px] text-muted italic py-1">No hay reparadores asignados.</div>
      ) : (
        <div className="space-y-3">
          {reparadores.map((r, idx) => (
            <div
              key={r.id || idx}
              data-testid={`reparador-${idx}`}
              className="bg-white border border-stone-200 rounded-md p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <Field label="Empresa" value={r.empresa} onChange={(v) => update(idx, "empresa", v)} testid={`rep-${idx}-empresa`} big />
                <div className="flex items-center gap-1 ml-2 self-start mt-3">
                  {r.directorio_id ? (
                    <span
                      className="text-[9px] font-bold uppercase tracking-wider bg-brand/10 text-brand px-1.5 py-0.5 rounded"
                      title="Vinculado al directorio centralizado"
                      data-testid={`reparador-${idx}-directorio-badge`}
                    >
                      ✓ Directorio
                    </span>
                  ) : (
                    <button
                      onClick={() => { setPickerForIdx(idx); setPickerOpen(true); }}
                      data-testid={`reparador-${idx}-link-directorio`}
                      className="text-[10px] text-muted hover:text-foreground px-1.5 py-0.5"
                      title="Vincular con directorio"
                    >
                      Vincular
                    </button>
                  )}
                  <button
                    onClick={() => remove(idx)}
                    data-testid={`reparador-${idx}-remove`}
                    className="text-muted hover:text-status-error-fg p-1"
                  >
                    <Trash className="w-3 h-3" weight="bold" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Contacto" value={r.contacto} onChange={(v) => update(idx, "contacto", v)} testid={`rep-${idx}-contacto`} />
                <Field label="Especialidad" value={r.especialidad} onChange={(v) => update(idx, "especialidad", v)} testid={`rep-${idx}-esp`} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Teléfono" value={r.telefono} onChange={(v) => update(idx, "telefono", v)} testid={`rep-${idx}-tel`} mono />
                <Field label="Email" value={r.email} onChange={(v) => update(idx, "email", v)} testid={`rep-${idx}-email`} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="CIF" value={r.cif} onChange={(v) => update(idx, "cif", v)} testid={`rep-${idx}-cif`} mono />
                <div>
                  <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold">Presupuesto €</label>
                  <input
                    type="number"
                    step="0.01"
                    value={r.importe_presupuesto ?? ""}
                    onChange={(e) => update(idx, "importe_presupuesto", e.target.value === "" ? null : parseFloat(e.target.value))}
                    placeholder="0,00"
                    data-testid={`rep-${idx}-presupuesto`}
                    className="!font-mono !text-[12px] !py-1"
                  />
                </div>
              </div>
              <Field label="Notas (resumen)" value={r.notas} onChange={(v) => update(idx, "notas", v)} testid={`rep-${idx}-notas`} />

              <ReparadorNotasLog
                notas={r.notas_log || []}
                onChange={(arr) => update(idx, "notas_log", arr)}
                idx={idx}
              />
            </div>
          ))}
        </div>
      )}

      {pickerOpen && (
        <DirectorioReparadoresPicker
          aseguradoraCode={aseguradoraCode}
          onPick={handlePick}
          onClose={() => { setPickerOpen(false); setPickerForIdx(null); }}
        />
      )}
    </SectionCard>
  );
}


// Selector de reparador desde el directorio centralizado.
// Filtra por la aseguradora del expediente si se conoce.

// Log cronológico de notas/avisos asociados a un reparador concreto
// dentro del expediente. Cada nota lleva su fecha (cliente) y autor (server)
// y se renderiza como un timeline simple. El usuario añade notas con "+".
function ReparadorNotasLog({ notas, onChange, idx }) {
  const [draft, setDraft] = useState("");
  const { user } = useAuth();

  // Ordenación cronológica memoizada — se recalculaba en cada keypress del
  // textarea de la nota nueva (mismo nodo React).
  const notasSorted = useMemo(
    () => (notas || []).slice().sort(
      (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
    ),
    [notas],
  );

  const add = () => {
    const texto = draft.trim();
    if (!texto) return;
    const nota = {
      id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())),
      texto,
      created_at: new Date().toISOString(),
      created_by: user?.id || null,
      created_by_name: user?.name || user?.code || null,
    };
    onChange([...(notas || []), nota]);
    setDraft("");
  };

  const remove = (noteId) => {
    if (!confirm("¿Eliminar esta nota?")) return;
    onChange((notas || []).filter((n) => n.id !== noteId));
  };

  const updateText = (noteId, texto) => {
    onChange((notas || []).map((n) => (n.id === noteId ? { ...n, texto } : n)));
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      add();
    }
  };

  return (
    <div className="mt-3 border-t border-stone-100 pt-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted inline-flex items-center gap-1">
          <ChatCircleText className="w-3 h-3" weight="bold" />
          Notas del reparador ({notas?.length || 0})
        </div>
      </div>

      {notas && notas.length > 0 && (
        <ul className="space-y-1.5 mb-2">
          {notasSorted.map((n) => (
            <li
              key={n.id}
              data-testid={`rep-${idx}-nota-${n.id}`}
              className="bg-stone-50 border-l-2 border-stone-300 rounded-r px-2.5 py-1.5"
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-[10px] text-muted font-mono">
                  {new Date(n.created_at).toLocaleString("es-ES", {
                    day: "2-digit", month: "2-digit", year: "2-digit",
                    hour: "2-digit", minute: "2-digit",
                  })}
                  {n.created_by_name && (
                    <span className="ml-1.5 text-[10px] text-muted/70">· {n.created_by_name}</span>
                  )}
                </div>
                <button
                  onClick={() => remove(n.id)}
                  data-testid={`rep-${idx}-nota-${n.id}-remove`}
                  className="text-muted hover:text-status-error-fg p-0.5"
                  title="Eliminar nota"
                >
                  <Trash className="w-3 h-3" weight="bold" />
                </button>
              </div>
              <textarea
                value={n.texto}
                onChange={(e) => updateText(n.id, e.target.value)}
                rows={2}
                className="!w-full !bg-transparent !border-none !p-0 !text-[12px] !text-foreground resize-none focus:outline-none mt-1"
              />
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-start gap-1.5">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          placeholder="Añadir nota del reparador (Cmd/Ctrl+Enter para guardar)..."
          data-testid={`rep-${idx}-nota-draft`}
          className="flex-1 !text-[12px] !py-1.5"
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          data-testid={`rep-${idx}-nota-add`}
          className="bg-stone-700 text-white text-[11px] px-2.5 py-1.5 rounded-md font-semibold inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed self-end"
          title="Añadir nota"
        >
          <Plus className="w-3 h-3" weight="bold" /> Añadir
        </button>
      </div>
    </div>
  );
}



function DirectorioReparadoresPicker({ aseguradoraCode, onPick, onClose }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState(aseguradoraCode ? "compania" : "todas"); // 'compania' | 'todas'

  useEffect(() => {
    let alive = true;
    const params = { limit: 50 };
    if (scope === "compania" && aseguradoraCode) params.compania = aseguradoraCode;
    if (q.trim()) params.q = q.trim();
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/reparadores", { params })
        .then((r) => { if (alive) setItems(r.data || []); })
        .catch(() => { if (alive) setItems([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [q, scope, aseguradoraCode]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        data-testid="directorio-picker-modal"
      >
        <div className="h-14 px-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">Directorio</div>
            <div className="font-display font-bold text-base tracking-tight">Buscar reparador</div>
          </div>
          <button onClick={onClose} className="p-2 text-muted hover:text-foreground">
            <XIcon className="w-4 h-4" weight="bold" />
          </button>
        </div>

        <div className="p-4 border-b border-border space-y-2">
          <input
            autoFocus
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nombre, CIF, teléfono, email, provincia..."
            data-testid="directorio-picker-search"
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
          />
          {aseguradoraCode && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setScope("compania")}
                data-testid="directorio-picker-scope-compania"
                className={`text-[11px] font-semibold px-2 py-1 rounded ${
                  scope === "compania"
                    ? "bg-foreground text-white"
                    : "bg-surface text-muted hover:text-foreground"
                }`}
              >
                Solo {aseguradoraCode}
              </button>
              <button
                onClick={() => setScope("todas")}
                data-testid="directorio-picker-scope-todas"
                className={`text-[11px] font-semibold px-2 py-1 rounded ${
                  scope === "todas"
                    ? "bg-foreground text-white"
                    : "bg-surface text-muted hover:text-foreground"
                }`}
              >
                Todas las compañías
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-sm text-muted text-center">Cargando...</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-muted text-center">Sin resultados.</div>
          ) : (
            <ul>
              {items.map((r) => (
                <li
                  key={r.id}
                  onClick={() => onPick(r)}
                  data-testid={`directorio-picker-item-${r.id}`}
                  className="px-5 py-3 border-b border-border last:border-b-0 hover:bg-surface cursor-pointer flex items-start justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{r.nombre}</div>
                    <div className="text-[11px] text-muted truncate mt-0.5">
                      {[r.provincia, r.telefonos?.[0], r.emails?.[0]].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 flex-shrink-0">
                    {(r.companias || []).slice(0, 3).map((c) => (
                      <span key={c} className="text-[9px] font-mono font-bold bg-brand/10 text-brand px-1.5 py-0.5 rounded">
                        {c}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}


// ====================================================================
// Selector de subencargo (debajo del Tipo encargo)
// Solo se habilita si el Tipo encargo es alguna Videoperitación.
// ====================================================================
function SubencargoSelector({ exp, data, onUpdateExpediente, naked = false }) {
  const aseguradoraName = data.aseguradora || exp.detected_aseguradora;
  const [aseguradoraId, setAseguradoraId] = useState(null);
  const [items, setItems] = useState([]);
  const currentId = exp.subencargo_id || "";

  // Resolver id de la aseguradora a partir del nombre
  useEffect(() => {
    if (!aseguradoraName) { setAseguradoraId(null); setItems([]); return; }
    let alive = true;
    api.get("/aseguradoras").then((r) => {
      if (!alive) return;
      const list = r.data || [];
      const a = list.find(
        (x) => x.name === aseguradoraName || x.nombre_compacto === aseguradoraName,
      );
      setAseguradoraId(a?.id || null);
    }).catch(() => setAseguradoraId(null));
    return () => { alive = false; };
  }, [aseguradoraName]);

  // Cargar subencargos
  useEffect(() => {
    if (!aseguradoraId) { setItems([]); return; }
    let alive = true;
    api.get(`/aseguradoras/${aseguradoraId}/subencargos`).then((r) => {
      if (!alive) return;
      setItems(r.data?.items || []);
    }).catch(() => setItems([]));
    return () => { alive = false; };
  }, [aseguradoraId]);

  const isVideoperitacion = (data.tipo_encargo || "")
    .toLowerCase()
    .includes("videoperitaci");
  // Subencargos aplicables: solo cuando el Tipo encargo es Videoperitación.
  const options = isVideoperitacion
    ? (() => {
        const matched = items.filter(
          (s) => (s.tipo || "").toLowerCase() === (data.tipo_encargo || "").toLowerCase(),
        );
        return matched.length ? matched : items.filter(
          (s) => (s.tipo || "").toLowerCase().includes("videoperitaci"),
        );
      })()
    : [];

  const handleChange = async (id) => {
    const sel = options.find((s) => s.id === id);
    try {
      await api.put(`/expedientes/${exp.id}`, {
        subencargo_id: id || null,
        subencargo_tipo: sel?.tipo || null,
        subencargo_subtipo: sel?.subtipo || null,
      });
      onUpdateExpediente?.("subencargo_id", id || null);
      onUpdateExpediente?.("subencargo_tipo", sel?.tipo || null);
      onUpdateExpediente?.("subencargo_subtipo", sel?.subtipo || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("No se pudo guardar el subencargo", e);
    }
  };

  // Si la aseguradora no tiene subencargos cargados, ocultamos el bloque
  if (!items.length) return naked ? <div className="text-[12px] text-muted italic h-[38px] flex items-center">No aplicable</div> : null;

  const disabled = !isVideoperitacion;
  return (
    <div>
      {!naked && (
        <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold">
          Subencargo
        </label>
      )}
      <select
        value={disabled ? "" : currentId}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled}
        data-testid="subencargo-select"
        className={`!text-[13px] !py-1.5 ${disabled ? "!bg-slate-100 !text-slate-400 cursor-not-allowed" : ""}`}
      >
        <option value="">
          {disabled
            ? "— Solo disponible para Videoperitación —"
            : "— Sin clasificar —"}
        </option>
        {!disabled && options.map((s) => (
          <option key={s.id} value={s.id}>
            {s.tipo}{s.subtipo ? ` · ${s.subtipo}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
